import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import EpsfPageBreadcrumb from "./EpsfPageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import Dropdown from "../../common/dropDown";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import { EpsfAccessGate, useEpsfAccess } from "./epsfAccess";
import { findOption, useEpsfStudySite } from "./epsfLedgerUi";
import {
  fetchFormTriggers,
  fetchFormOccurrences,
  fetchStorageUnits,
  saveFormRequirement,
  createManualFormOccurrence,
  fetchRolesForStudySite,
  saveBinderTriggerFiling,
  fetchBinderSections,
} from "./epsfService";
import { fetchTemplates } from "../esource/esourceService";
import { getUserData } from "../../../services/apiService";
import {
  fetchRoleLookup,
  roleIdToName,
} from "../../../services/lookupService";
import {
  EPSF_FORM_EVENTS,
  DUE_UNIT_OPTIONS,
  OVERDUE_UNIT_OPTIONS,
  blockingOptionsForEvent,
  eventCodeForTrigger,
  getEventMeta,
  isBlockingActionAllowed,
  minutesToOverdueParts,
  overdueToMinutes,
  previewGeneratedTitle,
  titleTokensForEvent,
} from "./epsfFormRequirementEvents";
import { formatFormTriggerLabel } from "./epsfBadges";
import {
  MANUAL_TRIGGER_OPTIONS,
  WEEKDAY_OPTIONS,
  REPORTING_PERIOD_OPTIONS,
  dayOfMonthOptions,
} from "./epsfFormManualTriggers";
import "./EpsfPages.scss";

const TABS = [
  { id: "event", label: "Event Forms", icon: "fas fa-bolt" },
  { id: "recurring", label: "Recurring Forms", icon: "fas fa-sync-alt" },
  { id: "manual", label: "Manual Forms", icon: "fas fa-hand-pointer" },
];

const EVENT_OPTIONS = EPSF_FORM_EVENTS.map((e) => ({
  value: e.value,
  label: e.label,
}));

const REQUIREMENT_LEVEL_OPTIONS = [
  { value: "OPTIONAL", label: "Optional" },
  { value: "REQUIRED", label: "Required" },
  { value: "REQUIRED_APPROVAL", label: "Required + Approval" },
];

/** Resolve blocking action: None when optional / unused; auto-pick when only one blocker. */
function pickBlockingAction(triggerCode, level, current = "") {
  if (level === "OPTIONAL") return "";
  const blockers = blockingOptionsForEvent(triggerCode, { includeNone: false });
  if (blockers.length === 0) return "";
  if (blockers.length === 1) return blockers[0].value;
  const cur = String(current || "").trim();
  if (blockers.some((b) => b.value === cur)) return cur;
  return "";
}

const FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

const SCOPE_OPTIONS = [
  { value: "STUDY_SITE", label: "Study / site" },
  { value: "STORAGE_UNIT", label: "Storage unit" },
];

const EVENT_FIELD_OPTIONS = [
  { key: "Event", label: "Event" },
  { key: "Form", label: "Form" },
  { key: "Requirement", label: "Requirement" },
  { key: "CompletedBy", label: "Completing role" },
  { key: "DueRule", label: "Due rule" },
  { key: "Blocks", label: "Blocks" },
  { key: "Reviewer", label: "Approver role" },
  { key: "Status", label: "Status" },
];
const EVENT_DEFAULT_COLUMNS = EVENT_FIELD_OPTIONS.map((f) => f.key);

const RECURRING_FIELD_OPTIONS = [
  { key: "Frequency", label: "Frequency" },
  { key: "Form", label: "Form" },
  { key: "Scope", label: "Scope" },
  { key: "CompletedBy", label: "Completing role" },
  { key: "Requirement", label: "Requirement" },
  { key: "DueRule", label: "Due rule" },
  { key: "Reviewer", label: "Approver role" },
  { key: "Status", label: "Status" },
];
const RECURRING_DEFAULT_COLUMNS = RECURRING_FIELD_OPTIONS.map((f) => f.key);

const RECURRING_TITLE_TOKENS = ["{Study}", "{Site}", "{Date}", "{Period}"];

const MANUAL_FIELD_OPTIONS = [
  { key: "Trigger", label: "Manual trigger" },
  { key: "Form", label: "Form" },
  { key: "Title", label: "Title" },
  { key: "Scope", label: "Scope" },
  { key: "Due", label: "Due" },
  { key: "Status", label: "Status" },
];
const MANUAL_DEFAULT_COLUMNS = MANUAL_FIELD_OPTIONS.map((f) => f.key);

const MANUAL_TITLE_TOKENS = ["{Study}", "{Site}", "{Date}"];

const MANUAL_MAX_ROWS = 10;

function todayLocalIsoDate() {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function emptyManualScheduleRow() {
  return {
    id: `ms-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    dueDate: todayLocalIsoDate(),
    dueTime: "17:00",
  };
}

/** Valid schedule slots: { dueDate, dueTime }[] */
function normalizeManualScheduleRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((r) => ({
      id: r?.id || emptyManualScheduleRow().id,
      dueDate: String(r?.dueDate || "").trim(),
      dueTime: String(r?.dueTime || "").trim(),
    }))
    .slice(0, MANUAL_MAX_ROWS);
}

function labelOf(options, value, fallback = "—") {
  const hit = (options || []).find(
    (o) => String(o.value) === String(value ?? ""),
  );
  return hit?.label || fallback;
}

function unitPhrase(code, value) {
  const n = Number(value);
  const qty = Number.isFinite(n) && n > 0 ? String(n) : "—";
  if (code === "WITHIN_MINUTES" || code === "MINUTES") {
    return `${qty} minute${n === 1 ? "" : "s"}`;
  }
  if (code === "WITHIN_DAYS" || code === "DAYS") {
    return `${qty} day${n === 1 ? "" : "s"}`;
  }
  return `${qty} hour${n === 1 ? "" : "s"}`;
}

/** Live "what will happen" story for the event requirement modal. */
function buildEventRequirementStory({
  form,
  templateLabel,
  completingRole,
  approverRole,
  blockingLabel,
  titlePreviewText,
}) {
  const eventLabel = labelOf(EVENT_OPTIONS, form.triggerCode, "the selected event");
  const levelLabel = labelOf(
    REQUIREMENT_LEVEL_OPTIONS,
    form.requirementLevel,
    "Optional",
  );
  const duePhrase = unitPhrase(form.dueRuleCode, form.dueRuleValue);
  const formName = templateLabel || "the selected form template";
  const who = completingRole || "the assigned completing role";
  const steps = [];

  steps.push({
    key: "when",
    title: "When it starts",
    text: `When "${eventLabel}" happens on this study and site, ePSF creates a due form from "${formName}".`,
  });

  if (form.generatedTitleTemplate || titlePreviewText) {
    steps.push({
      key: "title",
      title: "Form title",
      text: titlePreviewText
        ? `The generated form title will look like "${titlePreviewText}".`
        : `The form title template is "${form.generatedTitleTemplate}".`,
    });
  }

  steps.push({
    key: "who",
    title: "Who completes it",
    text: `Staff with the "${who}" role are expected to complete the form within ${duePhrase}.`,
  });

  if (form.requirementLevel === "OPTIONAL") {
    steps.push({
      key: "level",
      title: "Requirement",
      text: "This is optional — the workflow can continue even if the form is not completed.",
    });
  } else if (form.requirementLevel === "REQUIRED") {
    steps.push({
      key: "level",
      title: "Requirement",
      text: "This is required — the form must be completed to satisfy the requirement.",
    });
  } else {
    steps.push({
      key: "level",
      title: "Requirement",
      text: `This is "${levelLabel}" — completion alone is not enough; approval is also required.`,
    });
  }

  if (form.blockingActionCode && form.requirementLevel !== "OPTIONAL") {
    steps.push({
      key: "block",
      title: "Blocking",
      text: `Until the form is satisfied, "${blockingLabel || form.blockingActionCode}" stays blocked.`,
    });
  } else {
    steps.push({
      key: "block",
      title: "Blocking",
      text: "No inventory action is blocked by this requirement.",
    });
  }

  if (form.requirementLevel === "REQUIRED_APPROVAL") {
    const approver = approverRole || "the assigned approver role";
    const reviewDue = unitPhrase(
      form.reviewDueRuleCode,
      form.reviewDueRuleValue,
    );
    const correctionDue = unitPhrase(
      form.correctionDueRuleCode,
      form.correctionDueRuleValue,
    );
    steps.push({
      key: "approve",
      title: "Approval",
      text: `After submit, "${approver}" must approve within ${reviewDue}. If rejected, corrections are due within ${correctionDue}.`,
    });
  }

  const notifyBits = [];
  if (form.notifyOnDue) notifyBits.push("when the form becomes due");
  if (form.notifyOnOverdue) {
    notifyBits.push(
      `when overdue (after ${unitPhrase(form.overdueUnit, form.overdueValue)})`,
    );
  }
  steps.push({
    key: "email",
    title: "Email",
    text:
      notifyBits.length > 0
        ? `Email reminders are sent ${notifyBits.join(" and ")}.`
        : "No due or overdue email reminders are configured.",
  });

  return steps;
}

/** Live "what will happen" story for the recurring requirement modal (same pattern as event). */
function buildRecurringRequirementStory({
  form,
  templateLabel,
  completingRole,
  approverRole,
  titlePreviewText,
}) {
  const freqLabel = labelOf(FREQUENCY_OPTIONS, form.frequencyCode, "recurring");
  const scopeLabel = labelOf(
    SCOPE_OPTIONS,
    form.occurrenceScopeCode,
    "study / site",
  );
  const levelLabel = labelOf(
    REQUIREMENT_LEVEL_OPTIONS,
    form.requirementLevel,
    "Required",
  );
  const formName = templateLabel || "the selected form template";
  const who = completingRole || "the assigned completing role";
  const createAt = form.createLocalTime || "—";
  const dueAt = form.dueLocalTime || "—";
  const steps = [];

  let whenText = `On a ${freqLabel.toLowerCase()} schedule for this study and site (${scopeLabel}), ePSF creates a due form from "${formName}".`;
  if (form.frequencyCode === "WEEKLY") {
    const day = labelOf(WEEKDAY_OPTIONS, form.dayOfWeek, "the selected day");
    whenText = `Every week on ${day}, at ${createAt} local time, ePSF creates a due form from "${formName}" (scope: ${scopeLabel}).`;
  } else if (form.frequencyCode === "MONTHLY") {
    whenText = `Every month on day ${form.dayOfMonth || "—"}, at ${createAt} local time, ePSF creates a due form from "${formName}" (scope: ${scopeLabel}). Due day of month is ${form.dueDayOfMonth || "—"}.`;
  } else if (form.frequencyCode === "DAILY") {
    whenText = `Every day at ${createAt} local time, ePSF creates a due form from "${formName}" (scope: ${scopeLabel}).`;
  }
  steps.push({
    key: "when",
    title: "When it starts",
    text: whenText,
  });

  steps.push({
    key: "due",
    title: "Due time",
    text: `Each occurrence is due at ${dueAt} local time on the scheduled due day.`,
  });

  if (form.generatedTitleTemplate || titlePreviewText) {
    steps.push({
      key: "title",
      title: "Form title",
      text: titlePreviewText
        ? `The generated form title will look like "${titlePreviewText}".`
        : `The form title template is "${form.generatedTitleTemplate}".`,
    });
  }

  steps.push({
    key: "who",
    title: "Who completes it",
    text: `Staff with the "${who}" role are expected to complete the form by the due time.`,
  });

  if (form.requirementLevel === "OPTIONAL") {
    steps.push({
      key: "level",
      title: "Requirement",
      text: "This is optional — the workflow can continue even if the form is not completed.",
    });
  } else if (form.requirementLevel === "REQUIRED") {
    steps.push({
      key: "level",
      title: "Requirement",
      text: "This is required — the form must be completed to satisfy the requirement.",
    });
  } else {
    steps.push({
      key: "level",
      title: "Requirement",
      text: `This is "${levelLabel}" — completion alone is not enough; approval is also required.`,
    });
  }

  if (form.requirementLevel === "REQUIRED_APPROVAL") {
    const approver = approverRole || "the assigned approver role";
    const reviewDue = unitPhrase(
      form.reviewDueRuleCode,
      form.reviewDueRuleValue,
    );
    const correctionDue = unitPhrase(
      form.correctionDueRuleCode,
      form.correctionDueRuleValue,
    );
    steps.push({
      key: "approve",
      title: "Approval",
      text: `After submit, "${approver}" must approve within ${reviewDue}. If rejected, corrections are due within ${correctionDue}.`,
    });
  }

  const notifyBits = [];
  if (form.notifyOnDue) notifyBits.push("when the form becomes due");
  if (form.notifyOnOverdue) {
    notifyBits.push(
      `when overdue (after ${unitPhrase(form.overdueUnit, form.overdueValue)})`,
    );
  }
  steps.push({
    key: "email",
    title: "Email",
    text:
      notifyBits.length > 0
        ? `Email reminders are sent ${notifyBits.join(" and ")}.`
        : "No due or overdue email reminders are configured.",
  });

  return steps;
}

/** Live summary for manual (ad-hoc) schedule — up to 10 due date/time rows. */
function buildManualRequirementStory({
  form,
  templateLabel,
  completingRole,
  approverRole,
  titlePreviewText,
  scheduleRows,
}) {
  const triggerLabel = labelOf(
    MANUAL_TRIGGER_OPTIONS,
    form.manualTrigger,
    "the selected reason",
  );
  const scopeLabel = labelOf(
    SCOPE_OPTIONS,
    form.occurrenceScopeCode,
    "study / site",
  );
  const levelLabel = labelOf(
    REQUIREMENT_LEVEL_OPTIONS,
    form.requirementLevel,
    "Required",
  );
  const formName = templateLabel || "the selected form template";
  const who = completingRole || "the assigned completing role";
  const rows = normalizeManualScheduleRows(scheduleRows);
  const steps = [];

  if (rows.length <= 1) {
    const slot = rows[0] || {};
    steps.push({
      key: "when",
      title: "When it starts",
      text: `On ${slot.dueDate || "the selected date"} at ${slot.dueTime || "—"}, ePSF creates one due form from "${formName}" for "${triggerLabel}" (scope: ${scopeLabel}).`,
    });
  } else {
    const preview = rows
      .slice(0, 3)
      .map((r) => `${r.dueDate} ${r.dueTime}`)
      .join("; ");
    const more =
      rows.length > 3 ? ` and ${rows.length - 3} more` : "";
    steps.push({
      key: "when",
      title: "When it starts",
      text: `ePSF creates ${rows.length} due forms from "${formName}" for "${triggerLabel}" (scope: ${scopeLabel}) on these due slots: ${preview}${more}.`,
    });
  }

  if (form.generatedTitleTemplate || titlePreviewText) {
    steps.push({
      key: "title",
      title: "Form title",
      text: titlePreviewText
        ? `Each form title will look like "${titlePreviewText}" ({Date} updates per due slot).`
        : `The form title template is "${form.generatedTitleTemplate}".`,
    });
  }

  steps.push({
    key: "who",
    title: "Who completes it",
    text: `Staff with the "${who}" role are expected to complete each form by its due date and time.`,
  });

  if (form.requirementLevel === "OPTIONAL") {
    steps.push({
      key: "level",
      title: "Requirement",
      text: "This is optional — the workflow can continue even if the form is not completed.",
    });
  } else if (form.requirementLevel === "REQUIRED") {
    steps.push({
      key: "level",
      title: "Requirement",
      text: "This is required — each form must be completed to satisfy the requirement.",
    });
  } else {
    steps.push({
      key: "level",
      title: "Requirement",
      text: `This is "${levelLabel}" — completion alone is not enough; approval is also required.`,
    });
  }

  if (form.requirementLevel === "REQUIRED_APPROVAL") {
    const approver = approverRole || "the assigned approver role";
    const reviewDue = unitPhrase(
      form.reviewDueRuleCode,
      form.reviewDueRuleValue,
    );
    const correctionDue = unitPhrase(
      form.correctionDueRuleCode,
      form.correctionDueRuleValue,
    );
    steps.push({
      key: "approve",
      title: "Approval",
      text: `After submit, "${approver}" must approve within ${reviewDue}. If rejected, corrections are due within ${correctionDue}.`,
    });
  }

  if (String(form.manualReason || "").trim()) {
    steps.push({
      key: "reason",
      title: "Reason",
      text: `Reason recorded: "${String(form.manualReason).trim().slice(0, 160)}".`,
    });
  }

  const notifyBits = [];
  if (form.notifyOnDue) notifyBits.push("when each form becomes due");
  if (form.notifyOnOverdue) {
    notifyBits.push(
      `when overdue (after ${unitPhrase(form.overdueUnit, form.overdueValue)})`,
    );
  }
  steps.push({
    key: "email",
    title: "Email",
    text:
      notifyBits.length > 0
        ? `Email reminders are sent ${notifyBits.join(" and ")}.`
        : "No due or overdue email reminders are configured.",
  });

  return steps;
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <div className="epsf-form-field-error" role="alert">
      {message}
    </div>
  );
}

const GENERATED_TITLE_INFO = {
  title: "What is Generated form title?",
  body: [
    "This is the name shown on each form instance created by this requirement (in due forms lists, reminders, and the form header).",
    "Type plain text and optional tokens such as {ReceiptNo} or {Date}. Tokens are replaced with real values when the form is created.",
    "Leave blank to use the system default title for this event.",
  ],
};

function GeneratedTitleInfoHint() {
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const closeTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const placePopover = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 280;
    let left = rect.left;
    if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12);
    }
    setCoords({
      top: rect.bottom + 8,
      left,
    });
  }, []);

  const openPopover = useCallback(() => {
    clearCloseTimer();
    placePopover();
    setOpen(true);
  }, [clearCloseTimer, placePopover]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      const t = ev.target;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") setOpen(false);
    };
    const onScroll = () => placePopover();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, placePopover]);

  useEffect(
    () => () => {
      clearCloseTimer();
    },
    [clearCloseTimer],
  );

  return (
    <span className="epsf-form-req-info">
      <button
        ref={btnRef}
        type="button"
        className={`epsf-form-req-info__btn${open ? " is-open" : ""}`}
        aria-label={GENERATED_TITLE_INFO.title}
        aria-expanded={open}
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (open) setOpen(false);
          else openPopover();
        }}
        onMouseEnter={openPopover}
        onMouseLeave={scheduleClose}
        onFocus={openPopover}
        onBlur={scheduleClose}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={popRef}
              className="epsf-form-req-info__popover"
              role="tooltip"
              style={{ top: coords.top, left: coords.left }}
              onMouseEnter={clearCloseTimer}
              onMouseLeave={scheduleClose}
            >
              <div className="epsf-form-req-info__popover-title">
                {GENERATED_TITLE_INFO.title}
              </div>
              {GENERATED_TITLE_INFO.body.map((para) => (
                <p key={para} className="epsf-form-req-info__popover-text">
                  {para}
                </p>
              ))}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

function validateEventForm(form) {
  const errors = {};
  const level = form.requirementLevel || "OPTIONAL";
  const requiresApproval = level === "REQUIRED_APPROVAL";

  if (!String(form.triggerCode || "").trim()) {
    errors.triggerCode = "Event is required.";
  }
  if (!String(form.formTemplateId || "").trim()) {
    errors.formTemplateId = "Select a published e-Source template.";
  }
  if (!String(form.completedByRoleId || "").trim()) {
    errors.completedByRoleId = "Completing role is required.";
  }

  const dueVal = Number(form.dueRuleValue);
  if (!Number.isFinite(dueVal) || dueVal <= 0) {
    errors.dueRuleValue = "Due within must be greater than zero.";
  } else if (
    !["WITHIN_MINUTES", "WITHIN_HOURS", "WITHIN_DAYS"].includes(
      form.dueRuleCode,
    )
  ) {
    errors.dueRuleValue = "Select a valid due unit (Minutes, Hours, or Days).";
  }

  let blocking = form.blockingActionCode || "";
  if (level === "OPTIONAL") blocking = "";
  if (blocking && !isBlockingActionAllowed(form.triggerCode, blocking)) {
    errors.blockingActionCode =
      "That blocking action is not valid for the selected event.";
  }

  if (requiresApproval) {
    if (!String(form.reviewerRoleId || "").trim()) {
      errors.reviewerRoleId = "Approver role is required.";
    }
    if (
      !Number(form.reviewDueRuleValue) ||
      Number(form.reviewDueRuleValue) <= 0
    ) {
      errors.reviewDueRuleValue =
        "Approval due within must be greater than zero.";
    }
    if (
      !Number(form.correctionDueRuleValue) ||
      Number(form.correctionDueRuleValue) <= 0
    ) {
      errors.correctionDueRuleValue =
        "Correction due within must be greater than zero.";
    }
  }

  if (form.notifyOnOverdue) {
    const overdueMinutes = overdueToMinutes(
      form.overdueValue,
      form.overdueUnit,
    );
    if (!overdueMinutes || overdueMinutes <= 0) {
      errors.overdueValue = "Overdue email delay must be greater than zero.";
    }
  }

  return errors;
}

function validateRecurringForm(form) {
  const errors = {};
  const level = form.requirementLevel || "REQUIRED";
  const requiresApproval = level === "REQUIRED_APPROVAL";

  if (!String(form.frequencyCode || "").trim()) {
    errors.frequencyCode = "Frequency is required.";
  }
  if (!String(form.formTemplateId || "").trim()) {
    errors.formTemplateId = "Select a published e-Source template.";
  }
  if (!String(form.completedByRoleId || "").trim()) {
    errors.completedByRoleId = "Completing role is required.";
  }
  if (!form.createLocalTime) {
    errors.createLocalTime = "Create local time is required.";
  }
  if (!form.dueLocalTime) {
    errors.dueLocalTime = "Due local time is required.";
  }
  if (form.frequencyCode === "WEEKLY" && !form.dayOfWeek) {
    errors.dayOfWeek = "Day of week is required for weekly schedules.";
  }
  if (form.frequencyCode === "MONTHLY") {
    if (!form.dayOfMonth) {
      errors.dayOfMonth = "Create day of month is required.";
    }
    if (!form.dueDayOfMonth) {
      errors.dueDayOfMonth = "Due day of month is required.";
    }
  }
  if (requiresApproval) {
    if (!String(form.reviewerRoleId || "").trim()) {
      errors.reviewerRoleId = "Approver role is required.";
    }
    if (
      !Number(form.reviewDueRuleValue) ||
      Number(form.reviewDueRuleValue) <= 0
    ) {
      errors.reviewDueRuleValue =
        "Approval due within must be greater than zero.";
    }
    if (
      !Number(form.correctionDueRuleValue) ||
      Number(form.correctionDueRuleValue) <= 0
    ) {
      errors.correctionDueRuleValue =
        "Correction due within must be greater than zero.";
    }
  }
  if (form.notifyOnOverdue) {
    const overdueMinutes = overdueToMinutes(
      form.overdueValue,
      form.overdueUnit,
    );
    if (!overdueMinutes || overdueMinutes <= 0) {
      errors.overdueValue = "Overdue email delay must be greater than zero.";
    }
  }
  return errors;
}

function validateManualForm(form) {
  const errors = {};
  const level = form.requirementLevel || "REQUIRED";
  const requiresApproval = level === "REQUIRED_APPROVAL";
  const reason = String(form.manualReason || "").trim();

  if (!String(form.manualTrigger || "").trim()) {
    errors.manualTrigger = "Manual trigger / reason type is required.";
  }
  if (!String(form.formTemplateId || "").trim()) {
    errors.formTemplateId = "Select a published e-Source template.";
  }
  if (!String(form.completedByRoleId || "").trim()) {
    errors.completedByRoleId = "Completing role is required.";
  }
  if (form.occurrenceScopeCode === "STORAGE_UNIT" && !form.scopeEntityId) {
    errors.scopeEntityId = "Select a storage unit.";
  }
  const scheduleRows = normalizeManualScheduleRows(form.scheduleRows);
  if (!scheduleRows.length) {
    errors.scheduleRows = "Add at least one due date and time.";
  } else if (scheduleRows.length > MANUAL_MAX_ROWS) {
    errors.scheduleRows = `You can add up to ${MANUAL_MAX_ROWS} due slots.`;
  } else {
    const seen = new Set();
    scheduleRows.forEach((row, index) => {
      if (!row.dueDate) {
        errors[`scheduleRows.${index}.dueDate`] = "Due date is required.";
      }
      if (!row.dueTime) {
        errors[`scheduleRows.${index}.dueTime`] = "Due time is required.";
      }
      if (row.dueDate && row.dueTime) {
        const key = `${row.dueDate}T${row.dueTime}`;
        if (seen.has(key)) {
          errors[`scheduleRows.${index}.dueDate`] =
            "Duplicate due date and time.";
        }
        seen.add(key);
      }
    });
  }
  if (!reason) {
    errors.manualReason = "Reason / notes are required.";
  } else if (form.manualTrigger === "OTHER" && reason.length < 3) {
    errors.manualReason =
      "Please provide a reason when Manual Trigger is Other.";
  }
  if (requiresApproval) {
    if (!String(form.reviewerRoleId || "").trim()) {
      errors.reviewerRoleId = "Approver role is required.";
    }
    if (
      !Number(form.reviewDueRuleValue) ||
      Number(form.reviewDueRuleValue) <= 0
    ) {
      errors.reviewDueRuleValue =
        "Approval due within must be greater than zero.";
    }
    if (
      !Number(form.correctionDueRuleValue) ||
      Number(form.correctionDueRuleValue) <= 0
    ) {
      errors.correctionDueRuleValue =
        "Correction due within must be greater than zero.";
    }
  }
  if (form.notifyOnOverdue) {
    const overdueMinutes = overdueToMinutes(
      form.overdueValue,
      form.overdueUnit,
    );
    if (!overdueMinutes || overdueMinutes <= 0) {
      errors.overdueValue = "Overdue email delay must be greater than zero.";
    }
  }
  return errors;
}

function clearKeys(prev, keys) {
  if (!prev || !keys?.length) return prev || {};
  let changed = false;
  const next = { ...prev };
  keys.forEach((key) => {
    if (next[key]) {
      delete next[key];
      changed = true;
    }
  });
  return changed ? next : prev;
}

const DAY_OF_MONTH_OPTIONS = dayOfMonthOptions(28);

function requirementTypeOf(row) {
  const explicit = String(row.RequirementType || row.requirementType || "")
    .trim()
    .toUpperCase();
  if (explicit) return explicit;
  const code = String(row.TriggerCode || row.triggerCode || "").trim();
  if (code === "RecurringTemp" || code === "RECURRING") return "RECURRING";
  if (code === "Manual" || code === "MANUAL") return "MANUAL";
  return "EVENT";
}

const FORM_NAME_MAX_CHARS = 20;

function truncateFormName(name, max = FORM_NAME_MAX_CHARS) {
  const text = String(name || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}...`;
}

/** Form code badge + truncated name; full label in title tooltip. */
function renderFormTemplateCell(row) {
  const code = String(row.TemplateCode || row.templateCode || "").trim();
  const name = String(row.TemplateName || row.templateName || "").trim();
  if (!code && !name) return "—";
  const fullLabel = [code, name].filter(Boolean).join(" - ");
  const shortName = truncateFormName(name);
  return (
    <span className="epsf-form-req-form-cell" title={fullLabel}>
      {code ? (
        <span className="epsf-imp-product-cell__code">{code}</span>
      ) : null}
      {shortName ? (
        <span className="epsf-form-req-form-cell__name">{shortName}</span>
      ) : null}
    </span>
  );
}

function formatLevel(level) {
  const v = String(level || "OPTIONAL").toUpperCase();
  if (v === "REQUIRED_APPROVAL") return "Required + Approval";
  if (v === "REQUIRED") return "Required";
  return "Optional";
}

function formatDueRule(row) {
  const local = row.DueLocalTime || row.dueLocalTime;
  const code = row.DueRuleCode || row.dueRuleCode || "";
  const value = row.DueRuleValue ?? row.dueRuleValue;
  if (local && (!code || code === "AT_LOCAL_TIME")) {
    return `At ${String(local).slice(0, 5)}`;
  }
  if (!code && (value == null || value === "")) return "—";
  if (code === "WITHIN_HOURS" || !code) {
    return `Within ${value ?? "—"} hour(s)`;
  }
  if (code === "WITHIN_DAYS") return `Within ${value} day(s)`;
  if (code === "WITHIN_MINUTES") return `Within ${value} minute(s)`;
  return value != null && value !== "" ? `${code} ${value}` : code || "—";
}

function toTimeInput(value) {
  if (!value) return "";
  const s = String(value);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function emptyEventForm() {
  return {
    formTriggerId: null,
    triggerCode: "OnReceipt",
    formTemplateId: "",
    requirementLevel: "OPTIONAL",
    completedByRoleId: "",
    dueRuleCode: "WITHIN_HOURS",
    dueRuleValue: "2",
    blockingActionCode: "",
    reviewerRoleId: "",
    reviewDueRuleCode: "WITHIN_HOURS",
    reviewDueRuleValue: "1",
    correctionDueRuleCode: "WITHIN_HOURS",
    correctionDueRuleValue: "1",
    generatedTitleTemplate: "",
    notifyOnDue: true,
    notifyOnOverdue: true,
    overdueValue: "60",
    overdueUnit: "MINUTES",
    isActive: true,
    fileToBinder: false,
    binderSectionId: "",
    binderDocumentType: "Pharmacy Form",
  };
}

function emptyRecurringForm() {
  return {
    formTriggerId: null,
    formTemplateId: "",
    frequencyCode: "DAILY",
    occurrenceScopeCode: "STORAGE_UNIT",
    createLocalTime: "08:00",
    dueLocalTime: "10:00",
    dayOfWeek: "1",
    dueDayOfWeek: "1",
    dayOfMonth: "1",
    dueDayOfMonth: "5",
    reportingPeriodCode: "PREVIOUS_CALENDAR_MONTH",
    completedByRoleId: "",
    requirementLevel: "REQUIRED",
    reviewerRoleId: "",
    reviewDueRuleCode: "WITHIN_HOURS",
    reviewDueRuleValue: "24",
    correctionDueRuleCode: "WITHIN_HOURS",
    correctionDueRuleValue: "24",
    generatedTitleTemplate: "",
    notifyOnDue: true,
    notifyOnOverdue: true,
    overdueValue: "1",
    overdueUnit: "HOURS",
    isActive: true,
    fileToBinder: false,
    binderSectionId: "",
    binderDocumentType: "Pharmacy Form",
  };
}

function emptyManualCreateForm() {
  return {
    manualTrigger: "AD_HOC_STORAGE_INSPECTION",
    formTemplateId: "",
    occurrenceScopeCode: "STORAGE_UNIT",
    scopeEntityId: "",
    completedByRoleId: "",
    requirementLevel: "REQUIRED",
    reviewerRoleId: "",
    reviewDueRuleCode: "WITHIN_HOURS",
    reviewDueRuleValue: "24",
    correctionDueRuleCode: "WITHIN_HOURS",
    correctionDueRuleValue: "24",
    scheduleRows: [emptyManualScheduleRow()],
    generatedTitleTemplate: "",
    manualReason: "",
    notifyOnDue: true,
    notifyOnOverdue: true,
    overdueValue: "1",
    overdueUnit: "HOURS",
  };
}

export default function EpsfFormRequirements() {
  const { loading, canView, moduleAccess } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [tab, setTab] = useState("event");
  const [showFormModal, setShowFormModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const [eventForm, setEventForm] = useState(emptyEventForm);
  const [recurringForm, setRecurringForm] = useState(emptyRecurringForm);
  const [manualCreate, setManualCreate] = useState(emptyManualCreateForm);
  const [manualOccurrences, setManualOccurrences] = useState([]);
  const [storageUnits, setStorageUnits] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [orgRoleOptions, setOrgRoleOptions] = useState([]);
  const [binderSectionOptions, setBinderSectionOptions] = useState([]);
  const [eventFieldErrors, setEventFieldErrors] = useState({});
  const [recurringFieldErrors, setRecurringFieldErrors] = useState({});
  const [manualFieldErrors, setManualFieldErrors] = useState({});

  const rolesForNameLookup = useMemo(() => {
    const map = new Map();
    [...(roleOptions || []), ...(orgRoleOptions || [])].forEach((r) => {
      const id = String(r?.roleId || "").trim().toLowerCase();
      if (id) map.set(id, r);
    });
    return Array.from(map.values());
  }, [roleOptions, orgRoleOptions]);

  const templateOptions = useMemo(
    () =>
      templates.map((t) => {
        const id = String(t.FormTemplateId || t.formTemplateId);
        return {
          value: id,
          label: `${t.Code || t.code || ""} - ${t.Name || t.name || id}`,
        };
      }),
    [templates],
  );

  const templateOptionsWithPlaceholder = useMemo(
    () => [{ value: "", label: "Select template" }, ...templateOptions],
    [templateOptions],
  );

  const roleDropdownOptions = useMemo(() => {
    const selectedIds = [
      eventForm.completedByRoleId,
      eventForm.reviewerRoleId,
      recurringForm.completedByRoleId,
      recurringForm.reviewerRoleId,
      manualCreate.completedByRoleId,
      manualCreate.reviewerRoleId,
    ]
      .map((id) => String(id || "").trim())
      .filter(Boolean);

    const map = new Map();
    (roleOptions || []).forEach((r) => {
      const id = String(r.roleId || "").trim();
      if (!id) return;
      map.set(id.toLowerCase(), r);
    });
    selectedIds.forEach((id) => {
      const key = id.toLowerCase();
      if (map.has(key)) return;
      const fromOrg = (orgRoleOptions || []).find(
        (r) => String(r.roleId || "").toLowerCase() === key,
      );
      if (fromOrg) map.set(key, fromOrg);
    });

    const list = Array.from(map.values()).sort((a, b) =>
      String(a.roleName || "").localeCompare(String(b.roleName || "")),
    );

    return [
      { value: "", label: "Select role" },
      ...list.map((r) => ({
        value: String(r.roleId || ""),
        label: String(r.roleName || r.roleId || ""),
      })),
    ];
  }, [
    roleOptions,
    orgRoleOptions,
    eventForm.completedByRoleId,
    eventForm.reviewerRoleId,
    recurringForm.completedByRoleId,
    recurringForm.reviewerRoleId,
    manualCreate.completedByRoleId,
    manualCreate.reviewerRoleId,
  ]);

  const eventBlockingOptions = useMemo(() => {
    if (eventForm.requirementLevel === "OPTIONAL") {
      return [{ value: "", label: "None" }];
    }
    return blockingOptionsForEvent(eventForm.triggerCode, { includeNone: true });
  }, [eventForm.triggerCode, eventForm.requirementLevel]);

  const titleTokenHelp = useMemo(
    () => titleTokensForEvent(eventForm.triggerCode).join("  "),
    [eventForm.triggerCode],
  );

  const titlePreview = useMemo(
    () =>
      previewGeneratedTitle(eventForm.generatedTitleTemplate, {
        studyLabel:
          findOption(ctx.studyOptions, ctx.studyId)?.label || ctx.studyId,
        siteLabel:
          findOption(ctx.siteOptions, ctx.siteId)?.label || ctx.siteId,
      }),
    [
      eventForm.generatedTitleTemplate,
      ctx.studyId,
      ctx.siteId,
      ctx.studyOptions,
      ctx.siteOptions,
    ],
  );

  const eventRequirementStory = useMemo(() => {
    const templateLabel =
      templateOptions.find(
        (t) => String(t.value) === String(eventForm.formTemplateId || ""),
      )?.label || "";
    const completingRole = roleIdToName(
      eventForm.completedByRoleId,
      rolesForNameLookup,
    );
    const approverRole = roleIdToName(
      eventForm.reviewerRoleId,
      rolesForNameLookup,
    );
    const blockingLabel =
      eventBlockingOptions.find(
        (o) => String(o.value) === String(eventForm.blockingActionCode || ""),
      )?.label || "";
    return buildEventRequirementStory({
      form: eventForm,
      templateLabel,
      completingRole,
      approverRole,
      blockingLabel:
        blockingLabel && blockingLabel !== "None" ? blockingLabel : "",
      titlePreviewText: titlePreview || "",
    });
  }, [
    eventForm,
    templateOptions,
    rolesForNameLookup,
    eventBlockingOptions,
    titlePreview,
  ]);

  const recurringTitlePreview = useMemo(
    () =>
      previewGeneratedTitle(recurringForm.generatedTitleTemplate, {
        studyLabel:
          findOption(ctx.studyOptions, ctx.studyId)?.label || ctx.studyId,
        siteLabel:
          findOption(ctx.siteOptions, ctx.siteId)?.label || ctx.siteId,
        period:
          recurringForm.frequencyCode === "MONTHLY"
            ? labelOf(
                REPORTING_PERIOD_OPTIONS,
                recurringForm.reportingPeriodCode,
                "Period",
              )
            : recurringForm.frequencyCode === "WEEKLY"
              ? "Week"
              : "Day",
      }),
    [
      recurringForm.generatedTitleTemplate,
      recurringForm.frequencyCode,
      recurringForm.reportingPeriodCode,
      ctx.studyId,
      ctx.siteId,
      ctx.studyOptions,
      ctx.siteOptions,
    ],
  );

  const recurringRequirementStory = useMemo(() => {
    const templateLabel =
      templateOptions.find(
        (t) => String(t.value) === String(recurringForm.formTemplateId || ""),
      )?.label || "";
    const completingRole = roleIdToName(
      recurringForm.completedByRoleId,
      rolesForNameLookup,
    );
    const approverRole = roleIdToName(
      recurringForm.reviewerRoleId,
      rolesForNameLookup,
    );
    return buildRecurringRequirementStory({
      form: recurringForm,
      templateLabel,
      completingRole,
      approverRole,
      titlePreviewText: recurringTitlePreview || "",
    });
  }, [
    recurringForm,
    templateOptions,
    rolesForNameLookup,
    recurringTitlePreview,
  ]);

  const manualScheduleRows = useMemo(
    () => normalizeManualScheduleRows(manualCreate.scheduleRows),
    [manualCreate.scheduleRows],
  );

  const manualTitlePreview = useMemo(
    () =>
      previewGeneratedTitle(manualCreate.generatedTitleTemplate, {
        studyLabel:
          findOption(ctx.studyOptions, ctx.studyId)?.label || ctx.studyId,
        siteLabel:
          findOption(ctx.siteOptions, ctx.siteId)?.label || ctx.siteId,
        dateLabel: manualScheduleRows[0]?.dueDate || todayLocalIsoDate(),
      }),
    [
      manualCreate.generatedTitleTemplate,
      manualScheduleRows,
      ctx.studyId,
      ctx.siteId,
      ctx.studyOptions,
      ctx.siteOptions,
    ],
  );

  const manualRequirementStory = useMemo(() => {
    const templateLabel =
      templateOptions.find(
        (t) => String(t.value) === String(manualCreate.formTemplateId || ""),
      )?.label || "";
    const completingRole = roleIdToName(
      manualCreate.completedByRoleId,
      rolesForNameLookup,
    );
    const approverRole = roleIdToName(
      manualCreate.reviewerRoleId,
      rolesForNameLookup,
    );
    return buildManualRequirementStory({
      form: manualCreate,
      templateLabel,
      completingRole,
      approverRole,
      titlePreviewText: manualTitlePreview || "",
      scheduleRows: manualScheduleRows,
    });
  }, [
    manualCreate,
    templateOptions,
    rolesForNameLookup,
    manualTitlePreview,
    manualScheduleRows,
  ]);

  const refresh = useCallback(async () => {
    setError("");
    setListLoading(true);
    try {
      const tpl = await fetchTemplates();
      if (tpl.success) setTemplates(tpl.rows || []);

      if (!ctx.studyId || !ctx.siteId) {
        setTriggers([]);
        setManualOccurrences([]);
        setStorageUnits([]);
        return;
      }

      const [trig, occ, units] = await Promise.all([
        fetchFormTriggers(ctx.studyId, ctx.siteId, null),
        fetchFormOccurrences(ctx.studyId, ctx.siteId, null),
        fetchStorageUnits(ctx.studyId, ctx.siteId),
      ]);
      if (trig.success) setTriggers(trig.rows || []);
      else if (trig.message) setError(trig.message);

      if (occ.success) {
        const rows = occ.rows || [];
        setManualOccurrences(
          rows.filter((r) => {
            const init = String(
              r.InitiationType || r.initiationType || "",
            ).toUpperCase();
            const code = String(r.TriggerCode || r.triggerCode || "");
            return (
              init === "MANUAL" ||
              code === "Manual" ||
              code === "MANUAL" ||
              !!(r.ManualTrigger || r.manualTrigger)
            );
          }),
        );
      } else {
        setManualOccurrences([]);
      }
      if (units.success) {
        setStorageUnits(units.racks || units.rows || []);
      } else {
        setStorageUnits([]);
      }
    } catch (err) {
      setTriggers([]);
      setManualOccurrences([]);
      setError(
        err?.message === "Request timeout"
          ? "Timed out loading form requirements. Refresh and try again."
          : err?.message || "Could not load form requirements.",
      );
    } finally {
      setListLoading(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = getUserData() || {};
        const roles = await fetchRoleLookup(user);
        if (!cancelled) setOrgRoleOptions(Array.isArray(roles) ? roles : []);
      } catch {
        if (!cancelled) setOrgRoleOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchBinderSections();
        if (cancelled) return;
        const rows = res?.success ? res.rows || [] : [];
        const byId = new Map();
        rows.forEach((s) => {
          const id = String(s.BinderSectionId || s.binderSectionId || "");
          if (id) byId.set(id.toLowerCase(), s);
        });
        const opts = rows
          .filter((s) => s.ParentSectionId || s.parentSectionId)
          .map((s) => {
            const id = String(s.BinderSectionId || s.binderSectionId || "");
            const name = s.SectionName || s.sectionName || id;
            const parentId = String(
              s.ParentSectionId || s.parentSectionId || "",
            ).toLowerCase();
            const parent = byId.get(parentId);
            const parentName =
              parent?.SectionName ||
              parent?.sectionName ||
              s.ParentSectionName ||
              s.parentSectionName ||
              "";
            return {
              value: id,
              label: parentName ? `${parentName} › ${name}` : name,
            };
          })
          .filter((o) => o.value)
          .sort((a, b) => a.label.localeCompare(b.label));
        setBinderSectionOptions(opts);
      } catch {
        if (!cancelled) setBinderSectionOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ctx.studyId || !ctx.siteId) {
        setRoleOptions([]);
        return;
      }
      try {
        const roles = await fetchRolesForStudySite(ctx.studyId, ctx.siteId);
        if (!cancelled) setRoleOptions(Array.isArray(roles) ? roles : []);
      } catch {
        if (!cancelled) setRoleOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.studyId, ctx.siteId]);

  const eventRows = useMemo(
    () => triggers.filter((r) => requirementTypeOf(r) === "EVENT"),
    [triggers],
  );
  const recurringRows = useMemo(
    () => triggers.filter((r) => requirementTypeOf(r) === "RECURRING"),
    [triggers],
  );

  const storageUnitOptions = useMemo(
    () => [
      { value: "", label: "Select storage unit" },
      ...storageUnits.map((u) => {
        const id = String(u.StorageUnitId || u.storageUnitId || "");
        const label =
          `${u.UnitCode || u.unitCode || u.StorageUnitCode || ""} ${
            u.UnitName || u.unitName || u.StorageUnitName || ""
          }`.trim() || id;
        return { value: id, label };
      }),
    ],
    [storageUnits],
  );

  const patchEvent = (patch) => {
    setEventForm((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch || {});
    if (keys.includes("dueRuleCode")) keys.push("dueRuleValue");
    if (keys.includes("overdueUnit")) keys.push("overdueValue");
    if (keys.includes("reviewDueRuleCode")) keys.push("reviewDueRuleValue");
    if (keys.includes("correctionDueRuleCode"))
      keys.push("correctionDueRuleValue");
    setEventFieldErrors((prev) => clearKeys(prev, keys));
  };
  const patchRecurring = (patch) => {
    setRecurringForm((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch || {});
    if (keys.includes("overdueUnit")) keys.push("overdueValue");
    if (keys.includes("reviewDueRuleCode")) keys.push("reviewDueRuleValue");
    if (keys.includes("correctionDueRuleCode"))
      keys.push("correctionDueRuleValue");
    setRecurringFieldErrors((prev) => clearKeys(prev, keys));
  };
  const patchManualCreate = (patch) => {
    setManualCreate((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch || {});
    if (keys.includes("overdueUnit")) keys.push("overdueValue");
    if (keys.includes("reviewDueRuleCode")) keys.push("reviewDueRuleValue");
    if (keys.includes("correctionDueRuleCode"))
      keys.push("correctionDueRuleValue");
    setManualFieldErrors((prev) => clearKeys(prev, keys));
  };

  const onSaveEvent = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!ctx.studyId || !ctx.siteId) {
      setError("Select study and site.");
      return;
    }
    const errors = validateEventForm(eventForm);
    setEventFieldErrors(errors);
    if (Object.keys(errors).length) {
      setError("Please fix the highlighted fields.");
      requestAnimationFrame(() => {
        document
          .querySelector(
            ".epsf-form-req-modal .epsf-form-field-error, .epsf-form-req-modal .is-invalid",
          )
          ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
      return;
    }
    const level = eventForm.requirementLevel || "OPTIONAL";
    const requiresApproval = level === "REQUIRED_APPROVAL";
    let blocking = eventForm.blockingActionCode || "";
    if (level === "OPTIONAL") blocking = "";
    const dueVal = Number(eventForm.dueRuleValue);
    let overdueMinutes = 60;
    if (eventForm.notifyOnOverdue) {
      overdueMinutes = overdueToMinutes(
        eventForm.overdueValue,
        eventForm.overdueUnit,
      );
    }
    setBusy(true);
    try {
      const result = await saveFormRequirement({
        FormTriggerId: eventForm.formTriggerId || null,
        StudyId: ctx.studyId || null,
        SiteId: ctx.siteId || null,
        TriggerCode: eventForm.triggerCode,
        EventCode: eventCodeForTrigger(eventForm.triggerCode),
        RequirementType: "EVENT",
        FormTemplateId: eventForm.formTemplateId,
        RequirementLevel: level,
        CompletedByRoleId: eventForm.completedByRoleId,
        AssigneeRole: roleIdToName(eventForm.completedByRoleId, rolesForNameLookup) || null,
        DueRuleCode: eventForm.dueRuleCode,
        DueRuleValue: dueVal,
        BlockingActionCode: blocking || null,
        RequiresApproval: requiresApproval,
        ReviewerRoleId: requiresApproval ? eventForm.reviewerRoleId : null,
        ReviewerRole: requiresApproval
          ? roleIdToName(eventForm.reviewerRoleId, rolesForNameLookup) || null
          : null,
        ReviewDueRuleCode: requiresApproval
          ? eventForm.reviewDueRuleCode
          : null,
        ReviewDueRuleValue: requiresApproval
          ? Number(eventForm.reviewDueRuleValue)
          : null,
        CorrectionDueRuleCode: requiresApproval
          ? eventForm.correctionDueRuleCode
          : null,
        CorrectionDueRuleValue: requiresApproval
          ? Number(eventForm.correctionDueRuleValue)
          : null,
        GeneratedTitleTemplate: eventForm.generatedTitleTemplate.trim() || null,
        TitlePrefix: eventForm.generatedTitleTemplate.trim() || null,
        NotifyOnDue: eventForm.notifyOnDue,
        NotifyOnOverdue: eventForm.notifyOnOverdue,
        OverdueNotifyAfterMinutes: eventForm.notifyOnOverdue
          ? overdueMinutes
          : 60,
        IsActive: eventForm.isActive !== false,
      });
      if (!result.success) {
        setError(result.message || "Could not save form requirement.");
        return;
      }
      const savedTriggerId =
        result.rows?.[0]?.FormTriggerId ||
        result.rows?.[0]?.formTriggerId ||
        eventForm.formTriggerId ||
        null;
      if (savedTriggerId && eventForm.fileToBinder) {
        await saveBinderTriggerFiling({
          FormTriggerId: savedTriggerId,
          StudyId: ctx.studyId || null,
          SiteId: ctx.siteId || null,
          FileToBinder: true,
          BinderSectionId: eventForm.binderSectionId || null,
          DocumentType: eventForm.binderDocumentType || "Pharmacy Form",
        });
      } else if (savedTriggerId && eventForm.fileToBinder === false) {
        await saveBinderTriggerFiling({
          FormTriggerId: savedTriggerId,
          StudyId: ctx.studyId || null,
          SiteId: ctx.siteId || null,
          FileToBinder: false,
        });
      }
      setMessage(result.message || "Event form requirement saved.");
      setEventForm(emptyEventForm());
      setEventFieldErrors({});
      setShowFormModal(false);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onSaveRecurring = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!ctx.studyId || !ctx.siteId) {
      setError("Select study and site.");
      return;
    }
    const errors = validateRecurringForm(recurringForm);
    setRecurringFieldErrors(errors);
    if (Object.keys(errors).length) {
      setError("Please fix the highlighted fields.");
      requestAnimationFrame(() => {
        document
          .querySelector(
            ".epsf-form-req-modal .epsf-form-field-error, .epsf-form-req-modal .is-invalid",
          )
          ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
      return;
    }
    const level = recurringForm.requirementLevel || "REQUIRED";
    const requiresApproval = level === "REQUIRED_APPROVAL";
    let overdueMinutes = 60;
    if (recurringForm.notifyOnOverdue) {
      overdueMinutes = overdueToMinutes(
        recurringForm.overdueValue,
        recurringForm.overdueUnit,
      );
    }
    setBusy(true);
    try {
      const result = await saveFormRequirement({
        FormTriggerId: recurringForm.formTriggerId || null,
        StudyId: ctx.studyId || null,
        SiteId: ctx.siteId || null,
        TriggerCode: "RecurringTemp",
        RequirementType: "RECURRING",
        FormTemplateId: recurringForm.formTemplateId,
        FrequencyCode: recurringForm.frequencyCode,
        OccurrenceScopeCode: recurringForm.occurrenceScopeCode,
        CreateLocalTime: recurringForm.createLocalTime || null,
        DueLocalTime: recurringForm.dueLocalTime || null,
        DueRuleCode: "AT_LOCAL_TIME",
        DayOfWeek:
          recurringForm.frequencyCode === "WEEKLY"
            ? Number(recurringForm.dayOfWeek)
            : null,
        DueDayOfWeek:
          recurringForm.frequencyCode === "WEEKLY"
            ? Number(recurringForm.dueDayOfWeek || recurringForm.dayOfWeek)
            : null,
        DayOfMonth:
          recurringForm.frequencyCode === "MONTHLY"
            ? Number(recurringForm.dayOfMonth)
            : null,
        DueDayOfMonth:
          recurringForm.frequencyCode === "MONTHLY"
            ? Number(recurringForm.dueDayOfMonth)
            : null,
        ReportingPeriodCode:
          recurringForm.frequencyCode === "MONTHLY"
            ? recurringForm.reportingPeriodCode
            : null,
        CompletedByRoleId: recurringForm.completedByRoleId,
        AssigneeRole:
          roleIdToName(recurringForm.completedByRoleId, rolesForNameLookup) || null,
        RequirementLevel: level,
        RequiresApproval: requiresApproval,
        ReviewerRoleId: requiresApproval ? recurringForm.reviewerRoleId : null,
        ReviewerRole: requiresApproval
          ? roleIdToName(recurringForm.reviewerRoleId, rolesForNameLookup) || null
          : null,
        ReviewDueRuleCode: requiresApproval
          ? recurringForm.reviewDueRuleCode
          : null,
        ReviewDueRuleValue: requiresApproval
          ? Number(recurringForm.reviewDueRuleValue)
          : null,
        CorrectionDueRuleCode: requiresApproval
          ? recurringForm.correctionDueRuleCode
          : null,
        CorrectionDueRuleValue: requiresApproval
          ? Number(recurringForm.correctionDueRuleValue)
          : null,
        GeneratedTitleTemplate:
          recurringForm.generatedTitleTemplate.trim() || null,
        TitlePrefix: recurringForm.generatedTitleTemplate.trim() || null,
        NotifyOnDue: recurringForm.notifyOnDue,
        NotifyOnOverdue: recurringForm.notifyOnOverdue,
        OverdueNotifyAfterMinutes: recurringForm.notifyOnOverdue
          ? overdueMinutes
          : 60,
        IsActive: recurringForm.isActive !== false,
        CadenceDays:
          recurringForm.frequencyCode === "DAILY"
            ? 1
            : recurringForm.frequencyCode === "WEEKLY"
              ? 7
              : 30,
      });
      if (!result.success) {
        setError(result.message || "Could not save recurring requirement.");
        return;
      }
      setMessage(result.message || "Recurring form requirement saved.");
      setRecurringForm(emptyRecurringForm());
      setRecurringFieldErrors({});
      setShowFormModal(false);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onCreateManualOccurrence = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!ctx.studyId || !ctx.siteId) {
      setError("Select study and site.");
      return;
    }
    const errors = validateManualForm(manualCreate);
    setManualFieldErrors(errors);
    if (Object.keys(errors).length) {
      setError("Please fix the highlighted fields.");
      requestAnimationFrame(() => {
        document
          .querySelector(
            ".epsf-form-req-modal .epsf-form-field-error, .epsf-form-req-modal .is-invalid",
          )
          ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
      return;
    }
    const scheduleRows = normalizeManualScheduleRows(manualCreate.scheduleRows);
    if (!scheduleRows.length) {
      setError("Add at least one due date and time.");
      return;
    }

    const dayCount = scheduleRows.length;
    const confirmMessage =
      dayCount === 1
        ? `Save 1 manual due form for ${scheduleRows[0].dueDate} at ${scheduleRows[0].dueTime}? You must authenticate to complete.`
        : `Save ${dayCount} manual due forms with individual due dates and times? You must authenticate to complete.`;

    showAlert({
      title: "Confirm manual form requirement",
      message: confirmMessage,
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const auth = await openAuthModal({
          introMessage:
            "Re-authenticate to save this manual form requirement. Enter the reason for creating these due forms.",
          showDescription: true,
          requireDescription: true,
          descriptionLabel: "Reason",
          descriptionPlaceholder:
            "Reason for creating this manual form schedule…",
          descriptionMaxLength: 1000,
        });
        if (
          !auth ||
          auth.cancelled ||
          Number(auth.Success ?? auth.success ?? 0) !== 1
        ) {
          return;
        }
        const authReason = String(
          auth.Description || auth.description || "",
        ).trim();
        const formReason = String(manualCreate.manualReason || "").trim();
        const reason = authReason || formReason;
        if (!reason) {
          setError("Reason is required to save this manual form requirement.");
          return;
        }

        const level = manualCreate.requirementLevel || "REQUIRED";
        const requiresApproval = level === "REQUIRED_APPROVAL";
        let overdueMinutes = 60;
        if (manualCreate.notifyOnOverdue) {
          overdueMinutes = overdueToMinutes(
            manualCreate.overdueValue,
            manualCreate.overdueUnit,
          );
        }

        setBusy(true);
        try {
          let created = 0;
          let lastMessage = "";
          for (const slot of scheduleRows) {
            const dueLocal = `${slot.dueDate}T${slot.dueTime}`;
            const result = await createManualFormOccurrence({
              StudyId: ctx.studyId,
              SiteId: ctx.siteId,
              FormTemplateId: manualCreate.formTemplateId,
              ManualTrigger: manualCreate.manualTrigger,
              ManualReason: reason,
              ScopeEntityType: manualCreate.occurrenceScopeCode,
              ScopeEntityId:
                manualCreate.occurrenceScopeCode === "STORAGE_UNIT"
                  ? manualCreate.scopeEntityId
                  : null,
              CompletedByRoleId: manualCreate.completedByRoleId,
              RequirementLevel: level,
              RequiresApproval: requiresApproval,
              ReviewerRoleId: requiresApproval
                ? manualCreate.reviewerRoleId
                : null,
              ReviewDueRuleCode: requiresApproval
                ? manualCreate.reviewDueRuleCode
                : null,
              ReviewDueRuleValue: requiresApproval
                ? Number(manualCreate.reviewDueRuleValue)
                : null,
              CorrectionDueRuleCode: requiresApproval
                ? manualCreate.correctionDueRuleCode
                : null,
              CorrectionDueRuleValue: requiresApproval
                ? Number(manualCreate.correctionDueRuleValue)
                : null,
              Title: manualCreate.generatedTitleTemplate.trim() || null,
              GeneratedTitleTemplate:
                manualCreate.generatedTitleTemplate.trim() || null,
              DueAt: new Date(dueLocal).toISOString(),
              NotifyOnDue: manualCreate.notifyOnDue,
              NotifyOnOverdue: manualCreate.notifyOnOverdue,
              OverdueNotifyAfterMinutes: manualCreate.notifyOnOverdue
                ? overdueMinutes
                : 60,
            });
            if (!result.success) {
              setError(
                created > 0
                  ? `Saved ${created} of ${scheduleRows.length} forms, then failed: ${result.message || "Could not save manual form."}`
                  : result.message || "Could not save manual form.",
              );
              if (created > 0) await refresh();
              return;
            }
            created += 1;
            lastMessage = result.message || "";
          }
          setMessage(
            created === 1
              ? lastMessage || "Manual form requirement saved."
              : `Saved ${created} manual due forms.`,
          );
          setManualCreate(emptyManualCreateForm());
          setManualFieldErrors({});
          setShowFormModal(false);
          await refresh();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const loadEventRow = useCallback((row) => {
    const level = String(
      row.RequirementLevel || row.requirementLevel || "OPTIONAL",
    ).toUpperCase();
    const overdue = minutesToOverdueParts(
      row.OverdueNotifyAfterMinutes ?? row.overdueNotifyAfterMinutes ?? 60,
    );
    const triggerCode = String(row.TriggerCode || row.triggerCode || "OnReceipt");
    let blocking = row.BlockingActionCode || row.blockingActionCode || "";
    if (level === "OPTIONAL") blocking = "";
    if (blocking && !isBlockingActionAllowed(triggerCode, blocking)) {
      blocking = "";
    }
    setEventForm({
      formTriggerId: row.FormTriggerId || row.formTriggerId || null,
      triggerCode,
      formTemplateId: String(row.FormTemplateId || row.formTemplateId || ""),
      requirementLevel: level || "OPTIONAL",
      completedByRoleId: String(
        row.CompletedByRoleId || row.completedByRoleId || "",
      ),
      dueRuleCode: String(
        row.DueRuleCode || row.dueRuleCode || "WITHIN_HOURS",
      ),
      dueRuleValue: String(row.DueRuleValue ?? row.dueRuleValue ?? 2),
      blockingActionCode: blocking,
      reviewerRoleId: String(row.ReviewerRoleId || row.reviewerRoleId || ""),
      reviewDueRuleCode: String(
        row.ReviewDueRuleCode || row.reviewDueRuleCode || "WITHIN_HOURS",
      ),
      reviewDueRuleValue: String(
        row.ReviewDueRuleValue ?? row.reviewDueRuleValue ?? 1,
      ),
      correctionDueRuleCode: String(
        row.CorrectionDueRuleCode ||
          row.correctionDueRuleCode ||
          "WITHIN_HOURS",
      ),
      correctionDueRuleValue: String(
        row.CorrectionDueRuleValue ?? row.correctionDueRuleValue ?? 1,
      ),
      generatedTitleTemplate:
        row.GeneratedTitleTemplate ||
        row.generatedTitleTemplate ||
        row.TitlePrefix ||
        row.titlePrefix ||
        "",
      notifyOnDue: row.NotifyOnDue ?? row.notifyOnDue ?? true,
      notifyOnOverdue: row.NotifyOnOverdue ?? row.notifyOnOverdue ?? true,
      overdueValue: overdue.value,
      overdueUnit: overdue.unit,
      isActive: row.IsActive ?? row.isActive ?? true,
      fileToBinder: Boolean(row.FileToBinder ?? row.fileToBinder),
      binderSectionId: String(
        row.BinderSectionId || row.binderSectionId || "",
      ),
      binderDocumentType:
        row.BinderDocumentType ||
        row.binderDocumentType ||
        "Pharmacy Form",
    });
    setTab("event");
    setShowFormModal(true);
    setMessage("");
    setError("");
    setEventFieldErrors({});
  }, []);

  const loadRecurringRow = useCallback((row) => {
    const level = String(
      row.RequirementLevel || row.requirementLevel || "REQUIRED",
    ).toUpperCase();
    const overdue = minutesToOverdueParts(
      row.OverdueNotifyAfterMinutes ?? row.overdueNotifyAfterMinutes ?? 60,
    );
    setRecurringForm({
      formTriggerId: row.FormTriggerId || row.formTriggerId || null,
      formTemplateId: String(row.FormTemplateId || row.formTemplateId || ""),
      frequencyCode: String(
        row.FrequencyCode || row.frequencyCode || "DAILY",
      ).toUpperCase(),
      occurrenceScopeCode: String(
        row.OccurrenceScopeCode || row.occurrenceScopeCode || "STORAGE_UNIT",
      ).toUpperCase(),
      createLocalTime: toTimeInput(
        row.CreateLocalTime || row.createLocalTime || "08:00",
      ),
      dueLocalTime: toTimeInput(
        row.DueLocalTime || row.dueLocalTime || "10:00",
      ),
      dayOfWeek: String(row.DayOfWeek ?? row.dayOfWeek ?? "1"),
      dueDayOfWeek: String(row.DueDayOfWeek ?? row.dueDayOfWeek ?? "1"),
      dayOfMonth: String(row.DayOfMonth ?? row.dayOfMonth ?? "1"),
      dueDayOfMonth: String(row.DueDayOfMonth ?? row.dueDayOfMonth ?? "5"),
      reportingPeriodCode: String(
        row.ReportingPeriodCode ||
          row.reportingPeriodCode ||
          "PREVIOUS_CALENDAR_MONTH",
      ),
      completedByRoleId: String(
        row.CompletedByRoleId || row.completedByRoleId || "",
      ),
      requirementLevel: level || "REQUIRED",
      reviewerRoleId: String(row.ReviewerRoleId || row.reviewerRoleId || ""),
      reviewDueRuleCode: String(
        row.ReviewDueRuleCode || row.reviewDueRuleCode || "WITHIN_HOURS",
      ),
      reviewDueRuleValue: String(
        row.ReviewDueRuleValue ?? row.reviewDueRuleValue ?? 24,
      ),
      correctionDueRuleCode: String(
        row.CorrectionDueRuleCode ||
          row.correctionDueRuleCode ||
          "WITHIN_HOURS",
      ),
      correctionDueRuleValue: String(
        row.CorrectionDueRuleValue ?? row.correctionDueRuleValue ?? 24,
      ),
      generatedTitleTemplate:
        row.GeneratedTitleTemplate ||
        row.generatedTitleTemplate ||
        row.TitlePrefix ||
        row.titlePrefix ||
        "",
      notifyOnDue: row.NotifyOnDue ?? row.notifyOnDue ?? true,
      notifyOnOverdue: row.NotifyOnOverdue ?? row.notifyOnOverdue ?? true,
      overdueValue: overdue.value,
      overdueUnit: overdue.unit,
      isActive: row.IsActive ?? row.isActive ?? true,
    });
    setTab("recurring");
    setShowFormModal(true);
    setMessage("");
    setError("");
    setRecurringFieldErrors({});
  }, []);

  const closeFormModal = useCallback(() => {
    if (busy) return;
    setShowFormModal(false);
    setEventForm(emptyEventForm());
    setRecurringForm(emptyRecurringForm());
    setManualCreate(emptyManualCreateForm());
    setEventFieldErrors({});
    setRecurringFieldErrors({});
    setManualFieldErrors({});
    setError("");
  }, [busy]);

  const openAddFormModal = useCallback(() => {
    setError("");
    setMessage("");
    setEventFieldErrors({});
    setRecurringFieldErrors({});
    setManualFieldErrors({});
    if (tab === "event") setEventForm(emptyEventForm());
    else if (tab === "recurring") setRecurringForm(emptyRecurringForm());
    else setManualCreate(emptyManualCreateForm());
    setShowFormModal(true);
  }, [tab]);

  const switchTab = useCallback((nextTab) => {
    setTab(nextTab);
    setShowFormModal(false);
    setEventForm(emptyEventForm());
    setRecurringForm(emptyRecurringForm());
    setManualCreate(emptyManualCreateForm());
    setEventFieldErrors({});
    setRecurringFieldErrors({});
    setManualFieldErrors({});
    setError("");
  }, []);

  const renderEventCell = useCallback((row, field) => {
    if (field === "Event") {
      const code =
        row.TriggerCode ||
        row.triggerCode ||
        row.EventCode ||
        row.eventCode ||
        "";
      const meta = getEventMeta(code);
      if (meta?.label) return meta.label;
      return formatFormTriggerLabel(code) || code || "—";
    }
    if (field === "Form") return renderFormTemplateCell(row);
    if (field === "Requirement") {
      return formatLevel(row.RequirementLevel || row.requirementLevel);
    }
    if (field === "CompletedBy") {
      const id = row.CompletedByRoleId || row.completedByRoleId;
      return (
        roleIdToName(id, rolesForNameLookup) ||
        row.AssigneeRole ||
        row.assigneeRole ||
        "—"
      );
    }
    if (field === "DueRule") return formatDueRule(row);
    if (field === "Blocks") {
      const blockCode =
        row.BlockingActionCode || row.blockingActionCode || "";
      if (!blockCode) return "—";
      const trigger =
        row.TriggerCode || row.triggerCode || row.EventCode || row.eventCode;
      const blockers = blockingOptionsForEvent(trigger, { includeNone: false });
      const match = blockers.find((b) => b.value === blockCode);
      return match?.label || blockCode;
    }
    if (field === "Reviewer") {
      const id = row.ReviewerRoleId || row.reviewerRoleId;
      return (
        roleIdToName(id, rolesForNameLookup) ||
        row.ReviewerRole ||
        row.reviewerRole ||
        "—"
      );
    }
    if (field === "Status") {
      const active = row.IsActive ?? row.isActive;
      return active === false || active === 0 ? "Inactive" : "Active";
    }
    return "—";
  }, [rolesForNameLookup]);

  const renderRecurringCell = useCallback(
    (row, field) => {
      if (field === "Frequency") {
        return row.FrequencyCode || row.frequencyCode || "—";
      }
      if (field === "Form") return renderFormTemplateCell(row);
      if (field === "Scope") {
        return row.OccurrenceScopeCode || row.occurrenceScopeCode || "—";
      }
      if (field === "CompletedBy") {
        const id = row.CompletedByRoleId || row.completedByRoleId;
        return (
          roleIdToName(id, rolesForNameLookup) ||
          row.AssigneeRole ||
          row.assigneeRole ||
          "—"
        );
      }
      if (field === "Requirement") {
        return formatLevel(row.RequirementLevel || row.requirementLevel);
      }
      if (field === "DueRule") return formatDueRule(row);
      if (field === "Reviewer") {
        const id = row.ReviewerRoleId || row.reviewerRoleId;
        return (
          roleIdToName(id, rolesForNameLookup) ||
          row.ReviewerRole ||
          row.reviewerRole ||
          "—"
        );
      }
      if (field === "Status") {
        const active = row.IsActive ?? row.isActive;
        return active === false || active === 0 ? "Inactive" : "Active";
      }
      return "—";
    },
    [rolesForNameLookup],
  );

  const renderManualCell = useCallback((row, field) => {
    if (field === "Trigger") {
      const code = row.ManualTrigger || row.manualTrigger || "";
      const opt = MANUAL_TRIGGER_OPTIONS.find((x) => x.value === code);
      return opt?.label || code || "—";
    }
    if (field === "Form") return renderFormTemplateCell(row);
    if (field === "Title") {
      return row.Title || row.title || "—";
    }
    if (field === "Scope") {
      const scope = row.ScopeEntityType || row.scopeEntityType || "STUDY_SITE";
      return scope === "STORAGE_UNIT" ? "Storage unit" : "Study / site";
    }
    if (field === "Due") {
      const due = row.DueAtUtc || row.dueAtUtc || row.DueAt || row.dueAt;
      return due ? String(due).replace("T", " ").slice(0, 16) : "—";
    }
    if (field === "Status") {
      return (
        row.WorkflowStatus ||
        row.workflowStatus ||
        row.Status ||
        row.status ||
        "—"
      );
    }
    return "—";
  }, []);

  const eventRowActions = useMemo(
    () => [
      {
        label: "Edit",
        icon: "fas fa-edit",
        onClick: (row) => loadEventRow(row),
      },
    ],
    [loadEventRow],
  );
  const recurringRowActions = useMemo(
    () => [
      {
        label: "Edit",
        icon: "fas fa-edit",
        onClick: (row) => loadRecurringRow(row),
      },
    ],
    [loadRecurringRow],
  );

  const showApprovalFields = eventForm.requirementLevel === "REQUIRED_APPROVAL";
  const eventBlockingEnabled =
    eventForm.requirementLevel !== "OPTIONAL" &&
    blockingOptionsForEvent(eventForm.triggerCode, { includeNone: false })
      .length > 0;
  const showRecurringApproval =
    recurringForm.requirementLevel === "REQUIRED_APPROVAL";
  const showManualApproval =
    manualCreate.requirementLevel === "REQUIRED_APPROVAL";

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "Form requirements" },
          ]}
          actions={[
            {
              icon: "fas fa-plus",
              label:
                tab === "manual"
                  ? "Add manual requirement"
                  : tab === "recurring"
                    ? "Add recurring requirement"
                    : "Add event requirement",
              onClick: openAddFormModal,
            },
          ]}
        />
        <div className="epsf-receive">
          {error ? <p className="epsf-receive__error">{error}</p> : null}
          {message ? <p className="epsf-receive__ok">{message}</p> : null}

          <div className="epsf-form-req-shell">
            <div
              className="epsf-form-req-tabs"
              role="tablist"
              aria-label="Requirement type"
            >
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={`tab-item${tab === t.id ? " active" : ""}`}
                  onClick={() => switchTab(t.id)}
                >
                  <i className={t.icon} aria-hidden="true" />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>

            <div className="epsf-form-req-tab-body">
          {tab === "event" ? (
            <>
              <div className="epsf-report-wrap">
                <ReportTable
                  rows={eventRows}
                  loading={listLoading}
                  fieldOptions={EVENT_FIELD_OPTIONS}
                  defaultColumns={EVENT_DEFAULT_COLUMNS}
                  getFieldLabel={(field) =>
                    EVENT_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                    field
                  }
                  renderCell={renderEventCell}
                  rowActions={eventRowActions}
                  getRowKey={(row, index) =>
                    String(row.FormTriggerId || row.formTriggerId || index)
                  }
                  persistLayoutKey="epsf-form-req-event-v3"
                  emptyMessage="No event form requirements configured yet."
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              </div>
              {showFormModal ? (
                <div
                  className="modal fade show d-block epsf-modal"
                  tabIndex="-1"
                  role="dialog"
                  aria-modal="true"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <div className="modal-dialog modal-dialog-centered modal-xl epsf-form-req-modal">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title mb-0">{eventForm.formTriggerId ? "Edit event form requirement" : "Add event form requirement"}</h5>
                        <button
                          type="button"
                          className="btn-close"
                          aria-label="Close"
                          disabled={busy}
                          onClick={closeFormModal}
                        />
                      </div>
                      <form className="epsf-receive__form epsf-form-req-modal__form" onSubmit={onSaveEvent}>
                      <div className="modal-body">
                <div className="epsf-form-req-modal__layout">
                  <div className="epsf-form-req-modal__fields">
                <div className="row g-1">
                  {/* Row 1 — Event & form template */}
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Event <span className="mandatory">*</span>
                      </label>
                      <Dropdown
                        options={EVENT_OPTIONS}
                        selected={findOption(EVENT_OPTIONS, eventForm.triggerCode)}
                        className={
                          eventFieldErrors.triggerCode ? "is-invalid" : ""
                        }
                        onSelect={(opt) => {
                          const next = opt?.value || "OnReceipt";
                          patchEvent({
                            triggerCode: next,
                            blockingActionCode: pickBlockingAction(
                              next,
                              eventForm.requirementLevel,
                              eventForm.blockingActionCode,
                            ),
                          });
                        }}
                        showSearch={false}
                      />
                      <FieldError message={eventFieldErrors.triggerCode} />
                    </div>
                  </div>
                  <div className="col-12 col-md-8">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Form template <span className="mandatory">*</span>
                      </label>
                      <Dropdown
                        options={templateOptionsWithPlaceholder}
                        selected={findOption(
                          templateOptionsWithPlaceholder,
                          eventForm.formTemplateId,
                        )}
                        className={
                          eventFieldErrors.formTemplateId ? "is-invalid" : ""
                        }
                        onSelect={(opt) =>
                          patchEvent({ formTemplateId: opt?.value || "" })
                        }
                        showSearch
                      />
                      <FieldError message={eventFieldErrors.formTemplateId} />
                    </div>
                  </div>

                  {/* Row 2 — Completing role, Due within, due email */}
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Completing role <span className="mandatory">*</span>
                      </label>
                      <Dropdown
                        options={roleDropdownOptions}
                        selected={findOption(
                          roleDropdownOptions,
                          eventForm.completedByRoleId,
                        )}
                        className={
                          eventFieldErrors.completedByRoleId ? "is-invalid" : ""
                        }
                        onSelect={(opt) =>
                          patchEvent({ completedByRoleId: opt?.value || "" })
                        }
                        showSearch
                      />
                      <FieldError message={eventFieldErrors.completedByRoleId} />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Due within <span className="mandatory">*</span>
                      </label>
                      <div className="d-flex gap-2">
                        <input
                          className={`form-control${
                            eventFieldErrors.dueRuleValue ? " is-invalid" : ""
                          }`}
                          type="number"
                          min="1"
                          style={{ maxWidth: "6rem" }}
                          value={eventForm.dueRuleValue}
                          onChange={(ev) =>
                            patchEvent({ dueRuleValue: ev.target.value })
                          }
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Dropdown
                            options={DUE_UNIT_OPTIONS}
                            selected={findOption(
                              DUE_UNIT_OPTIONS,
                              eventForm.dueRuleCode,
                            )}
                            className={
                              eventFieldErrors.dueRuleValue ? "is-invalid" : ""
                            }
                            onSelect={(opt) =>
                              patchEvent({
                                dueRuleCode: opt?.value || "WITHIN_HOURS",
                              })
                            }
                            showSearch={false}
                          />
                        </div>
                      </div>
                      <FieldError message={eventFieldErrors.dueRuleValue} />
                    </div>
                  </div>
                  <div className="col-12 col-md-4 epsf-form-req-check-col">
                    <div className="epsf-form-field">
                      <label className="form-label epsf-form-check-label">
                        <input
                          type="checkbox"
                          checked={!!eventForm.notifyOnDue}
                          onChange={(ev) =>
                            patchEvent({ notifyOnDue: ev.target.checked })
                          }
                        />
                        <span>Send an email when this form is due?</span>
                      </label>
                    </div>
                    <div className="epsf-form-field mb-0">
                      <label className="form-label epsf-form-check-label">
                        <input
                          type="checkbox"
                          checked={!!eventForm.fileToBinder}
                          onChange={(ev) =>
                            patchEvent({ fileToBinder: ev.target.checked })
                          }
                        />
                        <span>File completed form in ePSF Binder</span>
                      </label>
                      {eventForm.fileToBinder ? (
                        <input
                          className="form-control form-control-sm mt-1"
                          placeholder="Document type (e.g. Receipt Checklist)"
                          value={eventForm.binderDocumentType || ""}
                          onChange={(ev) =>
                            patchEvent({
                              binderDocumentType: ev.target.value,
                            })
                          }
                        />
                      ) : null}
                      {eventForm.fileToBinder ? (
                        <p className="form-text mb-0 mt-1">
                          Choose the Binder folder where completed forms are
                          filed.
                        </p>
                      ) : null}
                      {eventForm.fileToBinder ? (
                        <select
                          className="form-select form-select-sm mt-1"
                          value={eventForm.binderSectionId || ""}
                          onChange={(ev) =>
                            patchEvent({
                              binderSectionId: ev.target.value,
                            })
                          }
                        >
                          <option value="">Select binder folder</option>
                          {binderSectionOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                  </div>

                  {/* Row 3 — Requirement, Blocking, Approver */}
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Requirement level <span className="mandatory">*</span>
                      </label>
                      <Dropdown
                        options={REQUIREMENT_LEVEL_OPTIONS}
                        selected={findOption(
                          REQUIREMENT_LEVEL_OPTIONS,
                          eventForm.requirementLevel,
                        )}
                        onSelect={(opt) => {
                          const level = opt?.value || "OPTIONAL";
                          patchEvent({
                            requirementLevel: level,
                            blockingActionCode: pickBlockingAction(
                              eventForm.triggerCode,
                              level,
                              eventForm.blockingActionCode,
                            ),
                            reviewerRoleId:
                              level === "REQUIRED_APPROVAL"
                                ? eventForm.reviewerRoleId
                                : "",
                          });
                        }}
                        showSearch={false}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">Blocking action</label>
                      <Dropdown
                        options={eventBlockingOptions}
                        selected={findOption(
                          eventBlockingOptions,
                          eventForm.blockingActionCode,
                        )}
                        disabled={!eventBlockingEnabled}
                        className={
                          eventFieldErrors.blockingActionCode ? "is-invalid" : ""
                        }
                        onSelect={(opt) =>
                          patchEvent({
                            blockingActionCode: opt?.value || "",
                          })
                        }
                        showSearch={false}
                      />
                      <FieldError message={eventFieldErrors.blockingActionCode} />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Approver role
                        {showApprovalFields ? (
                          <>
                            {" "}
                            <span className="mandatory">*</span>
                          </>
                        ) : null}
                      </label>
                      <Dropdown
                        options={roleDropdownOptions}
                        selected={findOption(
                          roleDropdownOptions,
                          eventForm.reviewerRoleId,
                        )}
                        disabled={!showApprovalFields}
                        className={
                          eventFieldErrors.reviewerRoleId ? "is-invalid" : ""
                        }
                        onSelect={(opt) =>
                          patchEvent({
                            reviewerRoleId: opt?.value || "",
                          })
                        }
                        showSearch
                      />
                      <FieldError message={eventFieldErrors.reviewerRoleId} />
                    </div>
                  </div>

                  {/* Row 4 — Approval / correction due */}
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Approval due within
                        {showApprovalFields ? (
                          <>
                            {" "}
                            <span className="mandatory">*</span>
                          </>
                        ) : null}
                      </label>
                      <div className="d-flex gap-2">
                        <input
                          className={`form-control${
                            eventFieldErrors.reviewDueRuleValue
                              ? " is-invalid"
                              : ""
                          }`}
                          type="number"
                          min="1"
                          style={{ maxWidth: "6rem" }}
                          value={eventForm.reviewDueRuleValue}
                          disabled={!showApprovalFields}
                          onChange={(ev) =>
                            patchEvent({
                              reviewDueRuleValue: ev.target.value,
                            })
                          }
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Dropdown
                            options={DUE_UNIT_OPTIONS}
                            selected={findOption(
                              DUE_UNIT_OPTIONS,
                              eventForm.reviewDueRuleCode,
                            )}
                            disabled={!showApprovalFields}
                            className={
                              eventFieldErrors.reviewDueRuleValue
                                ? "is-invalid"
                                : ""
                            }
                            onSelect={(opt) =>
                              patchEvent({
                                reviewDueRuleCode:
                                  opt?.value || "WITHIN_HOURS",
                              })
                            }
                            showSearch={false}
                          />
                        </div>
                      </div>
                      <FieldError
                        message={eventFieldErrors.reviewDueRuleValue}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Correction due within
                        {showApprovalFields ? (
                          <>
                            {" "}
                            <span className="mandatory">*</span>
                          </>
                        ) : null}
                      </label>
                      <div className="d-flex gap-2">
                        <input
                          className={`form-control${
                            eventFieldErrors.correctionDueRuleValue
                              ? " is-invalid"
                              : ""
                          }`}
                          type="number"
                          min="1"
                          style={{ maxWidth: "6rem" }}
                          value={eventForm.correctionDueRuleValue}
                          disabled={!showApprovalFields}
                          onChange={(ev) =>
                            patchEvent({
                              correctionDueRuleValue: ev.target.value,
                            })
                          }
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Dropdown
                            options={DUE_UNIT_OPTIONS}
                            selected={findOption(
                              DUE_UNIT_OPTIONS,
                              eventForm.correctionDueRuleCode,
                            )}
                            disabled={!showApprovalFields}
                            className={
                              eventFieldErrors.correctionDueRuleValue
                                ? "is-invalid"
                                : ""
                            }
                            onSelect={(opt) =>
                              patchEvent({
                                correctionDueRuleCode:
                                  opt?.value || "WITHIN_HOURS",
                              })
                            }
                            showSearch={false}
                          />
                        </div>
                      </div>
                      <FieldError
                        message={eventFieldErrors.correctionDueRuleValue}
                      />
                    </div>
                  </div>

                  {/* Same row as Approval/Correction — fills remaining col-md-4 */}
                  <div className="col-12 col-md-4 epsf-form-req-check-col">
                    <div className="epsf-form-field">
                      <label className="form-label epsf-form-check-label">
                        <input
                          type="checkbox"
                          checked={!!eventForm.notifyOnOverdue}
                          onChange={(ev) =>
                            patchEvent({ notifyOnOverdue: ev.target.checked })
                          }
                        />
                        <span>Send an email when this form is overdue?</span>
                      </label>
                    </div>
                  </div>

                  {/* Next row — title + overdue delay */}
                  <div className="col-12 col-md-8">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Generated form title <GeneratedTitleInfoHint />
                      </label>
                      <input
                        className="form-control"
                        value={eventForm.generatedTitleTemplate}
                        onChange={(ev) =>
                          patchEvent({
                            generatedTitleTemplate: ev.target.value,
                          })
                        }
                        placeholder="e.g. Receipt Checklist - {ReceiptNo}"
                      />
                      <div className="form-text">
                        Tokens: {titleTokenHelp}
                      </div>
                      {titlePreview ? (
                        <div className="form-text">
                          Preview: {titlePreview}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="epsf-form-field">
                      <label className="form-label">
                        Overdue email after
                        {eventForm.notifyOnOverdue ? (
                          <>
                            {" "}
                            <span className="mandatory">*</span>
                          </>
                        ) : null}
                      </label>
                      <div className="d-flex gap-2">
                        <input
                          className={`form-control${
                            eventFieldErrors.overdueValue ? " is-invalid" : ""
                          }`}
                          type="number"
                          min="1"
                          style={{ maxWidth: "6rem" }}
                          value={eventForm.overdueValue}
                          disabled={!eventForm.notifyOnOverdue}
                          onChange={(ev) =>
                            patchEvent({ overdueValue: ev.target.value })
                          }
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Dropdown
                            options={OVERDUE_UNIT_OPTIONS}
                            selected={findOption(
                              OVERDUE_UNIT_OPTIONS,
                              eventForm.overdueUnit,
                            )}
                            disabled={!eventForm.notifyOnOverdue}
                            className={
                              eventFieldErrors.overdueValue ? "is-invalid" : ""
                            }
                            onSelect={(opt) =>
                              patchEvent({
                                overdueUnit: opt?.value || "MINUTES",
                              })
                            }
                            showSearch={false}
                          />
                        </div>
                      </div>
                      <FieldError message={eventFieldErrors.overdueValue} />
                    </div>
                  </div>
                </div>
                  </div>
                  <aside
                    className="epsf-form-req-modal__story"
                    aria-label="Requirement summary"
                  >
                    <div className="epsf-form-req-modal__story-head">
                      <h6 className="epsf-form-req-modal__story-title">
                        What this will do
                      </h6>
                      <p className="epsf-form-req-modal__story-sub">
                        Live summary as you configure the requirement.
                      </p>
                    </div>
                    <ol className="epsf-form-req-modal__story-list">
                      {eventRequirementStory.map((step) => (
                        <li key={step.key} className="epsf-form-req-modal__story-item">
                          <span className="epsf-form-req-modal__story-item-title">
                            {step.title}
                          </span>
                          <p className="epsf-form-req-modal__story-item-text">
                            {step.text}
                          </p>
                        </li>
                      ))}
                    </ol>
                  </aside>
                </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={busy}
                          onClick={closeFormModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={busy}
                        >
                          {busy ? "Saving..." : "Save requirement"}
                        </button>
                      </div>
                      </form>
                    </div>
                  </div>
                </div>
              ) : null}

            </>
          ) : null}

          {tab === "recurring" ? (
            <>
              <div className="epsf-report-wrap">
                <ReportTable
                  rows={recurringRows}
                  loading={listLoading}
                  fieldOptions={RECURRING_FIELD_OPTIONS}
                  defaultColumns={RECURRING_DEFAULT_COLUMNS}
                  getFieldLabel={(field) =>
                    RECURRING_FIELD_OPTIONS.find((x) => x.key === field)
                      ?.label || field
                  }
                  renderCell={renderRecurringCell}
                  rowActions={recurringRowActions}
                  getRowKey={(row, index) =>
                    String(row.FormTriggerId || row.formTriggerId || index)
                  }
                  persistLayoutKey="epsf-form-req-recurring-v3"
                  emptyMessage="No recurring form requirements configured yet."
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              </div>
              {showFormModal ? (
                <div
                  className="modal fade show d-block epsf-modal"
                  tabIndex="-1"
                  role="dialog"
                  aria-modal="true"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <div className="modal-dialog modal-dialog-centered modal-xl epsf-form-req-modal">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title mb-0">
                          {recurringForm.formTriggerId
                            ? "Edit recurring form requirement"
                            : "Add recurring form requirement"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          aria-label="Close"
                          disabled={busy}
                          onClick={closeFormModal}
                        />
                      </div>
                      <form
                        className="epsf-receive__form epsf-form-req-modal__form"
                        onSubmit={onSaveRecurring}
                      >
                        <div className="modal-body">
                          <div className="epsf-form-req-modal__layout">
                            <div className="epsf-form-req-modal__fields">
                              <div className="row g-1">
                                {/* Row 1 — Frequency, scope, template */}
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Frequency{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={FREQUENCY_OPTIONS}
                                      selected={findOption(
                                        FREQUENCY_OPTIONS,
                                        recurringForm.frequencyCode,
                                      )}
                                      className={
                                        recurringFieldErrors.frequencyCode
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onSelect={(opt) =>
                                        patchRecurring({
                                          frequencyCode:
                                            opt?.value || "MONTHLY",
                                        })
                                      }
                                      showSearch={false}
                                    />
                                    <FieldError
                                      message={
                                        recurringFieldErrors.frequencyCode
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Occurrence scope{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={SCOPE_OPTIONS}
                                      selected={findOption(
                                        SCOPE_OPTIONS,
                                        recurringForm.occurrenceScopeCode,
                                      )}
                                      onSelect={(opt) =>
                                        patchRecurring({
                                          occurrenceScopeCode:
                                            opt?.value || "STUDY_SITE",
                                        })
                                      }
                                      showSearch={false}
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Form template{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={templateOptionsWithPlaceholder}
                                      selected={findOption(
                                        templateOptionsWithPlaceholder,
                                        recurringForm.formTemplateId,
                                      )}
                                      className={
                                        recurringFieldErrors.formTemplateId
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onSelect={(opt) =>
                                        patchRecurring({
                                          formTemplateId: opt?.value || "",
                                        })
                                      }
                                      showSearch
                                    />
                                    <FieldError
                                      message={
                                        recurringFieldErrors.formTemplateId
                                      }
                                    />
                                  </div>
                                </div>

                                {/* Row 2 — Completing role, create/due times */}
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Completing role{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={roleDropdownOptions}
                                      selected={findOption(
                                        roleDropdownOptions,
                                        recurringForm.completedByRoleId,
                                      )}
                                      className={
                                        recurringFieldErrors.completedByRoleId
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onSelect={(opt) =>
                                        patchRecurring({
                                          completedByRoleId: opt?.value || "",
                                        })
                                      }
                                      showSearch
                                    />
                                    <FieldError
                                      message={
                                        recurringFieldErrors.completedByRoleId
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Create local time{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <input
                                      className={`form-control${
                                        recurringFieldErrors.createLocalTime
                                          ? " is-invalid"
                                          : ""
                                      }`}
                                      type="time"
                                      value={recurringForm.createLocalTime}
                                      onChange={(ev) =>
                                        patchRecurring({
                                          createLocalTime: ev.target.value,
                                        })
                                      }
                                    />
                                    <FieldError
                                      message={
                                        recurringFieldErrors.createLocalTime
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Due local time{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <input
                                      className={`form-control${
                                        recurringFieldErrors.dueLocalTime
                                          ? " is-invalid"
                                          : ""
                                      }`}
                                      type="time"
                                      value={recurringForm.dueLocalTime}
                                      onChange={(ev) =>
                                        patchRecurring({
                                          dueLocalTime: ev.target.value,
                                        })
                                      }
                                    />
                                    <FieldError
                                      message={
                                        recurringFieldErrors.dueLocalTime
                                      }
                                    />
                                  </div>
                                </div>

                                {recurringForm.frequencyCode === "WEEKLY" ? (
                                  <div className="col-12 col-md-4">
                                    <div className="epsf-form-field">
                                      <label className="form-label">
                                        Day of week{" "}
                                        <span className="mandatory">*</span>
                                      </label>
                                      <Dropdown
                                        options={WEEKDAY_OPTIONS}
                                        selected={findOption(
                                          WEEKDAY_OPTIONS,
                                          recurringForm.dayOfWeek,
                                        )}
                                        className={
                                          recurringFieldErrors.dayOfWeek
                                            ? "is-invalid"
                                            : ""
                                        }
                                        onSelect={(opt) =>
                                          patchRecurring({
                                            dayOfWeek: opt?.value || "1",
                                            dueDayOfWeek: opt?.value || "1",
                                          })
                                        }
                                        showSearch={false}
                                      />
                                      <FieldError
                                        message={
                                          recurringFieldErrors.dayOfWeek
                                        }
                                      />
                                    </div>
                                  </div>
                                ) : null}
                                {recurringForm.frequencyCode === "MONTHLY" ? (
                                  <>
                                    <div className="col-12 col-md-4">
                                      <div className="epsf-form-field">
                                        <label className="form-label">
                                          Create day of month{" "}
                                          <span className="mandatory">*</span>
                                        </label>
                                        <Dropdown
                                          options={DAY_OF_MONTH_OPTIONS}
                                          selected={findOption(
                                            DAY_OF_MONTH_OPTIONS,
                                            recurringForm.dayOfMonth,
                                          )}
                                          className={
                                            recurringFieldErrors.dayOfMonth
                                              ? "is-invalid"
                                              : ""
                                          }
                                          onSelect={(opt) =>
                                            patchRecurring({
                                              dayOfMonth: opt?.value || "1",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                        <FieldError
                                          message={
                                            recurringFieldErrors.dayOfMonth
                                          }
                                        />
                                      </div>
                                    </div>
                                    <div className="col-12 col-md-4">
                                      <div className="epsf-form-field">
                                        <label className="form-label">
                                          Due day of month{" "}
                                          <span className="mandatory">*</span>
                                        </label>
                                        <Dropdown
                                          options={DAY_OF_MONTH_OPTIONS}
                                          selected={findOption(
                                            DAY_OF_MONTH_OPTIONS,
                                            recurringForm.dueDayOfMonth,
                                          )}
                                          className={
                                            recurringFieldErrors.dueDayOfMonth
                                              ? "is-invalid"
                                              : ""
                                          }
                                          onSelect={(opt) =>
                                            patchRecurring({
                                              dueDayOfMonth: opt?.value || "5",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                        <FieldError
                                          message={
                                            recurringFieldErrors.dueDayOfMonth
                                          }
                                        />
                                      </div>
                                    </div>
                                    <div className="col-12 col-md-4">
                                      <div className="epsf-form-field">
                                        <label className="form-label">
                                          Reporting period
                                        </label>
                                        <Dropdown
                                          options={REPORTING_PERIOD_OPTIONS}
                                          selected={findOption(
                                            REPORTING_PERIOD_OPTIONS,
                                            recurringForm.reportingPeriodCode,
                                          )}
                                          onSelect={(opt) =>
                                            patchRecurring({
                                              reportingPeriodCode:
                                                opt?.value ||
                                                "PREVIOUS_CALENDAR_MONTH",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                      </div>
                                    </div>
                                  </>
                                ) : null}

                                {/* Requirement + due email (same pattern as event) */}
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Requirement level{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={REQUIREMENT_LEVEL_OPTIONS}
                                      selected={findOption(
                                        REQUIREMENT_LEVEL_OPTIONS,
                                        recurringForm.requirementLevel,
                                      )}
                                      onSelect={(opt) => {
                                        const level = opt?.value || "REQUIRED";
                                        patchRecurring({
                                          requirementLevel: level,
                                          reviewerRoleId:
                                            level === "REQUIRED_APPROVAL"
                                              ? recurringForm.reviewerRoleId
                                              : "",
                                        });
                                      }}
                                      showSearch={false}
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4 epsf-form-req-check-col">
                                  <div className="epsf-form-field">
                                    <label className="form-label epsf-form-check-label">
                                      <input
                                        type="checkbox"
                                        checked={!!recurringForm.notifyOnDue}
                                        onChange={(ev) =>
                                          patchRecurring({
                                            notifyOnDue: ev.target.checked,
                                          })
                                        }
                                      />
                                      <span>
                                        Send an email when this form is due?
                                      </span>
                                    </label>
                                  </div>
                                </div>
                                <div className="col-12 col-md-4 epsf-form-req-check-col">
                                  <div className="epsf-form-field">
                                    <label className="form-label epsf-form-check-label">
                                      <input
                                        type="checkbox"
                                        checked={
                                          !!recurringForm.notifyOnOverdue
                                        }
                                        onChange={(ev) =>
                                          patchRecurring({
                                            notifyOnOverdue: ev.target.checked,
                                          })
                                        }
                                      />
                                      <span>
                                        Send an email when this form is
                                        overdue?
                                      </span>
                                    </label>
                                  </div>
                                </div>

                                {/* Approval fields — always visible, disabled unless Required + Approval */}
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Approver role
                                      {showRecurringApproval ? (
                                        <>
                                          {" "}
                                          <span className="mandatory">*</span>
                                        </>
                                      ) : null}
                                    </label>
                                    <Dropdown
                                      options={roleDropdownOptions}
                                      selected={findOption(
                                        roleDropdownOptions,
                                        recurringForm.reviewerRoleId,
                                      )}
                                      disabled={!showRecurringApproval}
                                      className={
                                        recurringFieldErrors.reviewerRoleId
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onSelect={(opt) =>
                                        patchRecurring({
                                          reviewerRoleId: opt?.value || "",
                                        })
                                      }
                                      showSearch
                                    />
                                    <FieldError
                                      message={
                                        recurringFieldErrors.reviewerRoleId
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Approval due within
                                      {showRecurringApproval ? (
                                        <>
                                          {" "}
                                          <span className="mandatory">*</span>
                                        </>
                                      ) : null}
                                    </label>
                                    <div className="d-flex gap-2">
                                      <input
                                        className={`form-control${
                                          recurringFieldErrors.reviewDueRuleValue
                                            ? " is-invalid"
                                            : ""
                                        }`}
                                        type="number"
                                        min="1"
                                        style={{ maxWidth: "6rem" }}
                                        value={
                                          recurringForm.reviewDueRuleValue
                                        }
                                        disabled={!showRecurringApproval}
                                        onChange={(ev) =>
                                          patchRecurring({
                                            reviewDueRuleValue:
                                              ev.target.value,
                                          })
                                        }
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Dropdown
                                          options={DUE_UNIT_OPTIONS}
                                          selected={findOption(
                                            DUE_UNIT_OPTIONS,
                                            recurringForm.reviewDueRuleCode,
                                          )}
                                          disabled={!showRecurringApproval}
                                          className={
                                            recurringFieldErrors.reviewDueRuleValue
                                              ? "is-invalid"
                                              : ""
                                          }
                                          onSelect={(opt) =>
                                            patchRecurring({
                                              reviewDueRuleCode:
                                                opt?.value || "WITHIN_HOURS",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                      </div>
                                    </div>
                                    <FieldError
                                      message={
                                        recurringFieldErrors.reviewDueRuleValue
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Correction due within
                                      {showRecurringApproval ? (
                                        <>
                                          {" "}
                                          <span className="mandatory">*</span>
                                        </>
                                      ) : null}
                                    </label>
                                    <div className="d-flex gap-2">
                                      <input
                                        className={`form-control${
                                          recurringFieldErrors.correctionDueRuleValue
                                            ? " is-invalid"
                                            : ""
                                        }`}
                                        type="number"
                                        min="1"
                                        style={{ maxWidth: "6rem" }}
                                        value={
                                          recurringForm.correctionDueRuleValue
                                        }
                                        disabled={!showRecurringApproval}
                                        onChange={(ev) =>
                                          patchRecurring({
                                            correctionDueRuleValue:
                                              ev.target.value,
                                          })
                                        }
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Dropdown
                                          options={DUE_UNIT_OPTIONS}
                                          selected={findOption(
                                            DUE_UNIT_OPTIONS,
                                            recurringForm.correctionDueRuleCode,
                                          )}
                                          disabled={!showRecurringApproval}
                                          className={
                                            recurringFieldErrors.correctionDueRuleValue
                                              ? "is-invalid"
                                              : ""
                                          }
                                          onSelect={(opt) =>
                                            patchRecurring({
                                              correctionDueRuleCode:
                                                opt?.value || "WITHIN_HOURS",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                      </div>
                                    </div>
                                    <FieldError
                                      message={
                                        recurringFieldErrors.correctionDueRuleValue
                                      }
                                    />
                                  </div>
                                </div>

                                {/* Title + overdue delay */}
                                <div className="col-12 col-md-8">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Generated form title{" "}
                                      <GeneratedTitleInfoHint />
                                    </label>
                                    <input
                                      className="form-control"
                                      value={
                                        recurringForm.generatedTitleTemplate
                                      }
                                      onChange={(ev) =>
                                        patchRecurring({
                                          generatedTitleTemplate:
                                            ev.target.value,
                                        })
                                      }
                                      placeholder="e.g. Temperature Log - {Date}"
                                    />
                                    <div className="form-text">
                                      Tokens: {RECURRING_TITLE_TOKENS.join("  ")}
                                    </div>
                                    {recurringTitlePreview ? (
                                      <div className="form-text">
                                        Preview: {recurringTitlePreview}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Overdue email after
                                      {recurringForm.notifyOnOverdue ? (
                                        <>
                                          {" "}
                                          <span className="mandatory">*</span>
                                        </>
                                      ) : null}
                                    </label>
                                    <div className="d-flex gap-2">
                                      <input
                                        className={`form-control${
                                          recurringFieldErrors.overdueValue
                                            ? " is-invalid"
                                            : ""
                                        }`}
                                        type="number"
                                        min="1"
                                        style={{ maxWidth: "6rem" }}
                                        value={recurringForm.overdueValue}
                                        disabled={
                                          !recurringForm.notifyOnOverdue
                                        }
                                        onChange={(ev) =>
                                          patchRecurring({
                                            overdueValue: ev.target.value,
                                          })
                                        }
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Dropdown
                                          options={OVERDUE_UNIT_OPTIONS}
                                          selected={findOption(
                                            OVERDUE_UNIT_OPTIONS,
                                            recurringForm.overdueUnit,
                                          )}
                                          disabled={
                                            !recurringForm.notifyOnOverdue
                                          }
                                          className={
                                            recurringFieldErrors.overdueValue
                                              ? "is-invalid"
                                              : ""
                                          }
                                          onSelect={(opt) =>
                                            patchRecurring({
                                              overdueUnit:
                                                opt?.value || "MINUTES",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                      </div>
                                    </div>
                                    <FieldError
                                      message={
                                        recurringFieldErrors.overdueValue
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <aside
                              className="epsf-form-req-modal__story"
                              aria-label="Requirement summary"
                            >
                              <div className="epsf-form-req-modal__story-head">
                                <h6 className="epsf-form-req-modal__story-title">
                                  What this will do
                                </h6>
                                <p className="epsf-form-req-modal__story-sub">
                                  Live summary as you configure the requirement.
                                </p>
                              </div>
                              <ol className="epsf-form-req-modal__story-list">
                                {recurringRequirementStory.map((step) => (
                                  <li
                                    key={step.key}
                                    className="epsf-form-req-modal__story-item"
                                  >
                                    <span className="epsf-form-req-modal__story-item-title">
                                      {step.title}
                                    </span>
                                    <p className="epsf-form-req-modal__story-item-text">
                                      {step.text}
                                    </p>
                                  </li>
                                ))}
                              </ol>
                            </aside>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={busy}
                            onClick={closeFormModal}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={busy}
                          >
                            {busy ? "Saving..." : "Save requirement"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              ) : null}

            </>
          ) : null}

          {tab === "manual" ? (
            <>
              <div className="epsf-report-wrap">
                <ReportTable
                  rows={manualOccurrences}
                  loading={listLoading}
                  fieldOptions={MANUAL_FIELD_OPTIONS}
                  defaultColumns={MANUAL_DEFAULT_COLUMNS}
                  getFieldLabel={(field) =>
                    MANUAL_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                    field
                  }
                  renderCell={renderManualCell}
                  getRowKey={(row, index) =>
                    String(
                      row.FormOccurrenceId || row.formOccurrenceId || index,
                    )
                  }
                  persistLayoutKey="epsf-form-req-manual-occ-v4"
                  emptyMessage="No manual forms created for this study/site yet."
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              </div>
              {showFormModal ? (
                <div
                  className="modal fade show d-block epsf-modal epsf-form-req-modal epsf-form-req-modal--full"
                  tabIndex="-1"
                  role="dialog"
                  aria-modal="true"
                >
                  <div className="modal-dialog epsf-form-req-modal__dialog">
                    <div className="modal-content epsf-form-req-modal__content">
                      <div className="modal-header">
                        <h5 className="modal-title mb-0">Add manual form requirement</h5>
                        <button
                          type="button"
                          className="btn-close"
                          aria-label="Close"
                          disabled={busy}
                          onClick={closeFormModal}
                        />
                      </div>
                      <form
                        className="epsf-receive__form epsf-form-req-modal__form"
                        onSubmit={onCreateManualOccurrence}
                      >
                        <div className="modal-body">
                          <div className="epsf-form-req-modal__layout epsf-form-req-modal__layout--with-schedule">
                            <div className="epsf-form-req-modal__fields">
                              <div className="row g-1">
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Manual trigger / reason type{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={MANUAL_TRIGGER_OPTIONS}
                                      selected={findOption(
                                        MANUAL_TRIGGER_OPTIONS,
                                        manualCreate.manualTrigger,
                                      )}
                                      className={
                                        manualFieldErrors.manualTrigger
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onSelect={(opt) =>
                                        patchManualCreate({
                                          manualTrigger:
                                            opt?.value ||
                                            "AD_HOC_STORAGE_INSPECTION",
                                        })
                                      }
                                      showSearch={false}
                                    />
                                    <FieldError
                                      message={manualFieldErrors.manualTrigger}
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-8">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Form template{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={templateOptionsWithPlaceholder}
                                      selected={findOption(
                                        templateOptionsWithPlaceholder,
                                        manualCreate.formTemplateId,
                                      )}
                                      className={
                                        manualFieldErrors.formTemplateId
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onSelect={(opt) =>
                                        patchManualCreate({
                                          formTemplateId: opt?.value || "",
                                        })
                                      }
                                      showSearch
                                    />
                                    <FieldError
                                      message={manualFieldErrors.formTemplateId}
                                    />
                                  </div>
                                </div>

                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Completing role{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={roleDropdownOptions}
                                      selected={findOption(
                                        roleDropdownOptions,
                                        manualCreate.completedByRoleId,
                                      )}
                                      className={
                                        manualFieldErrors.completedByRoleId
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onSelect={(opt) =>
                                        patchManualCreate({
                                          completedByRoleId: opt?.value || "",
                                        })
                                      }
                                      showSearch
                                    />
                                    <FieldError
                                      message={
                                        manualFieldErrors.completedByRoleId
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Occurrence scope{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={SCOPE_OPTIONS}
                                      selected={findOption(
                                        SCOPE_OPTIONS,
                                        manualCreate.occurrenceScopeCode,
                                      )}
                                      onSelect={(opt) =>
                                        patchManualCreate({
                                          occurrenceScopeCode:
                                            opt?.value || "STUDY_SITE",
                                          scopeEntityId: "",
                                        })
                                      }
                                      showSearch={false}
                                    />
                                  </div>
                                </div>
                                {manualCreate.occurrenceScopeCode ===
                                "STORAGE_UNIT" ? (
                                  <div className="col-12 col-md-4">
                                    <div className="epsf-form-field">
                                      <label className="form-label">
                                        Storage unit{" "}
                                        <span className="mandatory">*</span>
                                      </label>
                                      <Dropdown
                                        options={storageUnitOptions}
                                        selected={findOption(
                                          storageUnitOptions,
                                          manualCreate.scopeEntityId,
                                        )}
                                        className={
                                          manualFieldErrors.scopeEntityId
                                            ? "is-invalid"
                                            : ""
                                        }
                                        onSelect={(opt) =>
                                          patchManualCreate({
                                            scopeEntityId: opt?.value || "",
                                          })
                                        }
                                        showSearch
                                      />
                                      <FieldError
                                        message={manualFieldErrors.scopeEntityId}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="col-12 col-md-4 epsf-form-req-check-col">
                                    <div className="epsf-form-field">
                                      <label className="form-label epsf-form-check-label">
                                        <input
                                          type="checkbox"
                                          checked={!!manualCreate.notifyOnDue}
                                          onChange={(ev) =>
                                            patchManualCreate({
                                              notifyOnDue: ev.target.checked,
                                            })
                                          }
                                        />
                                        <span>
                                          Send an email when this form is due?
                                        </span>
                                      </label>
                                    </div>
                                  </div>
                                )}

                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Requirement level{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <Dropdown
                                      options={REQUIREMENT_LEVEL_OPTIONS}
                                      selected={findOption(
                                        REQUIREMENT_LEVEL_OPTIONS,
                                        manualCreate.requirementLevel,
                                      )}
                                      onSelect={(opt) => {
                                        const level = opt?.value || "REQUIRED";
                                        patchManualCreate({
                                          requirementLevel: level,
                                          reviewerRoleId:
                                            level === "REQUIRED_APPROVAL"
                                              ? manualCreate.reviewerRoleId
                                              : "",
                                        });
                                      }}
                                      showSearch={false}
                                    />
                                  </div>
                                </div>
                                {manualCreate.occurrenceScopeCode ===
                                "STORAGE_UNIT" ? (
                                  <div className="col-12 col-md-4 epsf-form-req-check-col">
                                    <div className="epsf-form-field">
                                      <label className="form-label epsf-form-check-label">
                                        <input
                                          type="checkbox"
                                          checked={!!manualCreate.notifyOnDue}
                                          onChange={(ev) =>
                                            patchManualCreate({
                                              notifyOnDue: ev.target.checked,
                                            })
                                          }
                                        />
                                        <span>
                                          Send an email when this form is due?
                                        </span>
                                      </label>
                                    </div>
                                  </div>
                                ) : null}
                                <div className="col-12 col-md-4 epsf-form-req-check-col">
                                  <div className="epsf-form-field">
                                    <label className="form-label epsf-form-check-label">
                                      <input
                                        type="checkbox"
                                        checked={!!manualCreate.notifyOnOverdue}
                                        onChange={(ev) =>
                                          patchManualCreate({
                                            notifyOnOverdue: ev.target.checked,
                                          })
                                        }
                                      />
                                      <span>
                                        Send an email when this form is overdue?
                                      </span>
                                    </label>
                                  </div>
                                </div>

                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Approver role
                                      {showManualApproval ? (
                                        <>
                                          {" "}
                                          <span className="mandatory">*</span>
                                        </>
                                      ) : null}
                                    </label>
                                    <Dropdown
                                      options={roleDropdownOptions}
                                      selected={findOption(
                                        roleDropdownOptions,
                                        manualCreate.reviewerRoleId,
                                      )}
                                      disabled={!showManualApproval}
                                      className={
                                        manualFieldErrors.reviewerRoleId
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onSelect={(opt) =>
                                        patchManualCreate({
                                          reviewerRoleId: opt?.value || "",
                                        })
                                      }
                                      showSearch
                                    />
                                    <FieldError
                                      message={manualFieldErrors.reviewerRoleId}
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Approval due within
                                      {showManualApproval ? (
                                        <>
                                          {" "}
                                          <span className="mandatory">*</span>
                                        </>
                                      ) : null}
                                    </label>
                                    <div className="d-flex gap-2">
                                      <input
                                        className={`form-control${
                                          manualFieldErrors.reviewDueRuleValue
                                            ? " is-invalid"
                                            : ""
                                        }`}
                                        type="number"
                                        min="1"
                                        style={{ maxWidth: "6rem" }}
                                        value={manualCreate.reviewDueRuleValue}
                                        disabled={!showManualApproval}
                                        onChange={(ev) =>
                                          patchManualCreate({
                                            reviewDueRuleValue: ev.target.value,
                                          })
                                        }
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Dropdown
                                          options={DUE_UNIT_OPTIONS}
                                          selected={findOption(
                                            DUE_UNIT_OPTIONS,
                                            manualCreate.reviewDueRuleCode,
                                          )}
                                          disabled={!showManualApproval}
                                          className={
                                            manualFieldErrors.reviewDueRuleValue
                                              ? "is-invalid"
                                              : ""
                                          }
                                          onSelect={(opt) =>
                                            patchManualCreate({
                                              reviewDueRuleCode:
                                                opt?.value || "WITHIN_HOURS",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                      </div>
                                    </div>
                                    <FieldError
                                      message={
                                        manualFieldErrors.reviewDueRuleValue
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Correction due within
                                      {showManualApproval ? (
                                        <>
                                          {" "}
                                          <span className="mandatory">*</span>
                                        </>
                                      ) : null}
                                    </label>
                                    <div className="d-flex gap-2">
                                      <input
                                        className={`form-control${
                                          manualFieldErrors.correctionDueRuleValue
                                            ? " is-invalid"
                                            : ""
                                        }`}
                                        type="number"
                                        min="1"
                                        style={{ maxWidth: "6rem" }}
                                        value={
                                          manualCreate.correctionDueRuleValue
                                        }
                                        disabled={!showManualApproval}
                                        onChange={(ev) =>
                                          patchManualCreate({
                                            correctionDueRuleValue:
                                              ev.target.value,
                                          })
                                        }
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Dropdown
                                          options={DUE_UNIT_OPTIONS}
                                          selected={findOption(
                                            DUE_UNIT_OPTIONS,
                                            manualCreate.correctionDueRuleCode,
                                          )}
                                          disabled={!showManualApproval}
                                          className={
                                            manualFieldErrors.correctionDueRuleValue
                                              ? "is-invalid"
                                              : ""
                                          }
                                          onSelect={(opt) =>
                                            patchManualCreate({
                                              correctionDueRuleCode:
                                                opt?.value || "WITHIN_HOURS",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                      </div>
                                    </div>
                                    <FieldError
                                      message={
                                        manualFieldErrors.correctionDueRuleValue
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="col-12 col-md-8">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Generated form title{" "}
                                      <GeneratedTitleInfoHint />
                                    </label>
                                    <input
                                      className="form-control"
                                      value={manualCreate.generatedTitleTemplate}
                                      onChange={(ev) =>
                                        patchManualCreate({
                                          generatedTitleTemplate:
                                            ev.target.value,
                                        })
                                      }
                                      placeholder="Ad-hoc storage inspection {Date}"
                                    />
                                    <div className="form-text">
                                      Tokens: {MANUAL_TITLE_TOKENS.join("  ")}
                                    </div>
                                    {manualTitlePreview ? (
                                      <div className="form-text">
                                        Preview: {manualTitlePreview}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Overdue email after
                                      {manualCreate.notifyOnOverdue ? (
                                        <>
                                          {" "}
                                          <span className="mandatory">*</span>
                                        </>
                                      ) : null}
                                    </label>
                                    <div className="d-flex gap-2">
                                      <input
                                        className={`form-control${
                                          manualFieldErrors.overdueValue
                                            ? " is-invalid"
                                            : ""
                                        }`}
                                        type="number"
                                        min="1"
                                        style={{ maxWidth: "6rem" }}
                                        value={manualCreate.overdueValue}
                                        disabled={!manualCreate.notifyOnOverdue}
                                        onChange={(ev) =>
                                          patchManualCreate({
                                            overdueValue: ev.target.value,
                                          })
                                        }
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Dropdown
                                          options={OVERDUE_UNIT_OPTIONS}
                                          selected={findOption(
                                            OVERDUE_UNIT_OPTIONS,
                                            manualCreate.overdueUnit,
                                          )}
                                          disabled={!manualCreate.notifyOnOverdue}
                                          className={
                                            manualFieldErrors.overdueValue
                                              ? "is-invalid"
                                              : ""
                                          }
                                          onSelect={(opt) =>
                                            patchManualCreate({
                                              overdueUnit:
                                                opt?.value || "MINUTES",
                                            })
                                          }
                                          showSearch={false}
                                        />
                                      </div>
                                    </div>
                                    <FieldError
                                      message={manualFieldErrors.overdueValue}
                                    />
                                  </div>
                                </div>

                                <div className="col-12">
                                  <div className="epsf-form-field">
                                    <label className="form-label">
                                      Reason / notes{" "}
                                      <span className="mandatory">*</span>
                                    </label>
                                    <textarea
                                      className={`form-control${
                                        manualFieldErrors.manualReason
                                          ? " is-invalid"
                                          : ""
                                      }`}
                                      rows={3}
                                      value={manualCreate.manualReason}
                                      onChange={(ev) =>
                                        patchManualCreate({
                                          manualReason: ev.target.value,
                                        })
                                      }
                                    />
                                    <FieldError
                                      message={manualFieldErrors.manualReason}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <aside
                              className="epsf-form-req-modal__schedule"
                              aria-label="Manual due date and time"
                            >
                              <div className="epsf-form-req-modal__schedule-head">
                                <div className="epsf-form-req-modal__schedule-head-text">
                                  <h6 className="epsf-form-req-modal__schedule-title">
                                    Due date and time{" "}
                                    <span className="mandatory">*</span>
                                  </h6>
                                  <p className="epsf-form-req-modal__schedule-sub">
                                    Up to {MANUAL_MAX_ROWS} accurate due slots
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary epsf-form-req-modal__schedule-add"
                                  disabled={
                                    busy ||
                                    manualScheduleRows.length >= MANUAL_MAX_ROWS
                                  }
                                  onClick={() => {
                                    if (
                                      manualScheduleRows.length >=
                                      MANUAL_MAX_ROWS
                                    ) {
                                      return;
                                    }
                                    patchManualCreate({
                                      scheduleRows: [
                                        ...manualScheduleRows,
                                        emptyManualScheduleRow(),
                                      ],
                                    });
                                  }}
                                >
                                  <i
                                    className="fas fa-plus"
                                    aria-hidden="true"
                                  />{" "}
                                  Add row
                                </button>
                              </div>
                              <div className="epsf-form-req-schedule-table">
                                <div
                                  className="epsf-form-req-schedule-table__head"
                                  aria-hidden="true"
                                >
                                  <span>#</span>
                                  <span>Due date</span>
                                  <span>Due time</span>
                                  <span />
                                </div>
                                <div className="epsf-form-req-schedule-table__body">
                                  {manualScheduleRows.map((row, index) => (
                                    <div
                                      key={row.id}
                                      className="epsf-form-req-schedule-table__row"
                                    >
                                      <span className="epsf-form-req-schedule-table__idx">
                                        {index + 1}
                                      </span>
                                      <div className="epsf-form-req-schedule-table__cell">
                                        <input
                                          id={`manual-due-date-${row.id}`}
                                          className={`form-control${
                                            manualFieldErrors[
                                              `scheduleRows.${index}.dueDate`
                                            ]
                                              ? " is-invalid"
                                              : ""
                                          }`}
                                          type="date"
                                          value={row.dueDate}
                                          aria-label={`Due date ${index + 1}`}
                                          onChange={(ev) => {
                                            const next = [
                                              ...manualScheduleRows,
                                            ];
                                            next[index] = {
                                              ...next[index],
                                              dueDate: ev.target.value,
                                            };
                                            patchManualCreate({
                                              scheduleRows: next,
                                            });
                                          }}
                                        />
                                        <FieldError
                                          message={
                                            manualFieldErrors[
                                              `scheduleRows.${index}.dueDate`
                                            ]
                                          }
                                        />
                                      </div>
                                      <div className="epsf-form-req-schedule-table__cell">
                                        <input
                                          id={`manual-due-time-${row.id}`}
                                          className={`form-control${
                                            manualFieldErrors[
                                              `scheduleRows.${index}.dueTime`
                                            ]
                                              ? " is-invalid"
                                              : ""
                                          }`}
                                          type="time"
                                          value={row.dueTime}
                                          aria-label={`Due time ${index + 1}`}
                                          onChange={(ev) => {
                                            const next = [
                                              ...manualScheduleRows,
                                            ];
                                            next[index] = {
                                              ...next[index],
                                              dueTime: ev.target.value,
                                            };
                                            patchManualCreate({
                                              scheduleRows: next,
                                            });
                                          }}
                                        />
                                        <FieldError
                                          message={
                                            manualFieldErrors[
                                              `scheduleRows.${index}.dueTime`
                                            ]
                                          }
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-sm epsf-form-req-schedule-table__remove"
                                        title="Remove row"
                                        aria-label={`Remove due slot ${index + 1}`}
                                        disabled={
                                          busy ||
                                          manualScheduleRows.length <= 1
                                        }
                                        onClick={() => {
                                          if (
                                            manualScheduleRows.length <= 1
                                          ) {
                                            return;
                                          }
                                          patchManualCreate({
                                            scheduleRows:
                                              manualScheduleRows.filter(
                                                (_, i) => i !== index,
                                              ),
                                          });
                                        }}
                                      >
                                        <i
                                          className="fas fa-trash"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <FieldError
                                message={manualFieldErrors.scheduleRows}
                              />
                            </aside>
                            <aside
                              className="epsf-form-req-modal__story"
                              aria-label="Requirement summary"
                            >
                              <div className="epsf-form-req-modal__story-head">
                                <h6 className="epsf-form-req-modal__story-title">
                                  What this will do
                                </h6>
                                <p className="epsf-form-req-modal__story-sub">
                                  Live summary as you configure the schedule.
                                </p>
                              </div>
                              <ol className="epsf-form-req-modal__story-list">
                                {manualRequirementStory.map((step) => (
                                  <li
                                    key={step.key}
                                    className="epsf-form-req-modal__story-item"
                                  >
                                    <span className="epsf-form-req-modal__story-item-title">
                                      {step.title}
                                    </span>
                                    <p className="epsf-form-req-modal__story-item-text">
                                      {step.text}
                                    </p>
                                  </li>
                                ))}
                              </ol>
                            </aside>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={busy}
                            onClick={closeFormModal}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={busy}
                          >
                            {busy ? "Saving..." : "Save requirement"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              ) : null}

            </>
          ) : null}
            </div>
          </div>
        </div>
      </div>
      <InfoAlert {...alertState} onClose={hideAlert} />
    </EpsfAccessGate>
  );
}
